import React, { useState, useEffect, useRef } from 'react';
import { Stage, Layer, Line, Rect, Text, Circle, Image as KonvaImage, Transformer, Group } from 'react-konva';
import { 
  Save, Trash2, Grid, Square, MousePointer, Ruler, PenTool, Type, Eraser, 
  Image, Upload, Eye, EyeOff, Plus, Minus, X, Layers, Compass, Zap,
  ArrowLeft, Maximize2, SlidersHorizontal, ChevronRight, Check, Package, RotateCw,
  Undo2, Redo2, Circle as CircleIcon, DoorOpen, AppWindow
} from 'lucide-react';
import * as sketchService from '../services/sketchService';
import toast from 'react-hot-toast';
import DimensionLine from './sketch/DimensionLine';
import TechnicalPoint, { TECHNICAL_POINTS_CATALOG } from './sketch/TechnicalIcons';
import ApplianceElement from './sketch/ApplianceElement';
import ApplianceCatalogDrawer from './sketch/ApplianceCatalogDrawer';
import CalcoControls from './sketch/CalcoControls';

const SketchEditor = ({ projectId, onClose }) => {
  const [project, setProject] = useState(null);
  const [tool, setTool] = useState('select');
  const [selectedId, setSelectedId] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState(null);
  const [showGrid, setShowGrid] = useState(true);
  const [draggingId, setDraggingId] = useState(null);
  const [freehandPoints, setFreehandPoints] = useState([]);
  const [brushColor, setBrushColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(2);
  const [penMode, setPenMode] = useState('freehand'); // 'freehand' ou 'line'

  // Estados para páginas e camadas (como Photoshop)
  const [pages, setPages] = useState([
    {
      id: 'page-1',
      name: 'Página 1',
      layers: [
        { id: 'layer-1', name: 'Camada 1', visible: true, elements: [] }
      ]
    }
  ]);
  const [activePageId, setActivePageId] = useState('page-1');
  const [activeLayerId, setActiveLayerId] = useState('layer-1');

  // Histórico para Desfazer (Undo / Ctrl+Z) e Refazer (Redo / Ctrl+Y)
  const [history, setHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(-1);
  const isUndoRedoActionRef = useRef(false);

  // Efeito para registrar estados de histórico para Undo / Redo
  useEffect(() => {
    if (isUndoRedoActionRef.current) {
      isUndoRedoActionRef.current = false;
      return;
    }
    // Grava snapshot simplificado das páginas
    const snapshot = JSON.stringify(pages);
    setHistory(prev => {
      const currentHistory = prev.slice(0, historyStep + 1);
      // Evita duplicatas consecutivas
      if (currentHistory.length > 0 && currentHistory[currentHistory.length - 1] === snapshot) {
        return prev;
      }
      const newHist = [...currentHistory, snapshot].slice(-30); // Limite de 30 estados na memória
      setHistoryStep(newHist.length - 1);
      return newHist;
    });
  }, [pages]);

  const handleUndo = () => {
    if (historyStep > 0) {
      const targetStep = historyStep - 1;
      const snapshot = history[targetStep];
      if (snapshot) {
        try {
          const restoredPages = JSON.parse(snapshot);
          isUndoRedoActionRef.current = true;
          setPages(restoredPages);
          setHistoryStep(targetStep);
          setSelectedId(null);
        } catch (err) {
          console.warn('Erro ao restaurar histórico Undo:', err);
        }
      }
    }
  };

  const handleRedo = () => {
    if (historyStep < history.length - 1) {
      const targetStep = historyStep + 1;
      const snapshot = history[targetStep];
      if (snapshot) {
        try {
          const restoredPages = JSON.parse(snapshot);
          isUndoRedoActionRef.current = true;
          setPages(restoredPages);
          setHistoryStep(targetStep);
          setSelectedId(null);
        } catch (err) {
          console.warn('Erro ao restaurar histórico Redo:', err);
        }
      }
    }
  };

  const activePageIdRef = useRef('page-1');
  const activeLayerIdRef = useRef('layer-1');

  useEffect(() => {
    activePageIdRef.current = activePageId;
  }, [activePageId]);

  useEffect(() => {
    activeLayerIdRef.current = activeLayerId;
  }, [activeLayerId]);

  const [showTextInput, setShowTextInput] = useState(false);
  const [textInputPos, setTextInputPos] = useState({ x: 0, y: 0 });
  const [textInputValue, setTextInputValue] = useState('');
  const textInputValueRef = useRef('');
  const textInputRef = useRef(null);
  const idCounterRef = useRef(1);
  const dragStartRef = useRef(null);
  const stageRef = useRef(null);

  const bgImageRef = useRef(null);
  const bgImageGroupRef = useRef(null);
  const transformerRef = useRef(null);
  const fileInputRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const [stageSize, setStageSize] = useState({ width: 1200, height: 800 });

  // Estados de Zoom e Pan via Konva
  const [stageScale, setStageScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const isSpacePressedRef = useRef(false);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef(null);

  // Referências para Pinch Zoom & Pan via Touch (Tablets / Celulares)
  const touchStartDistRef = useRef(null);
  const touchStartCenterRef = useRef(null);
  const touchStartScaleRef = useRef(null);
  const touchStartStagePosRef = useRef(null);
  const isTouchPinchingRef = useRef(false);
  const isTouchPanningRef = useRef(false);
  const touchPanStartRef = useRef(null);

  // Função utilitária para converter coordenadas da tela/Stage para o mundo do canvas
  const getPointerWorldPosition = (stageInstance = null) => {
    const stage = stageInstance || stageRef.current;
    if (!stage) return { x: 0, y: 0 };
    const pointerPos = stage.getPointerPosition();
    if (!pointerPos) return { x: 0, y: 0 };

    const transform = stage.getAbsoluteTransform().copy().invert();
    return transform.point(pointerPos);
  };

  // Snap magnético suave para encontrar vértices / extremidades próximas de linhas existentes
  const getSnappedPoint = (pos, tolerance = 18) => {
    if (!pos) return pos;
    const allElements = activePage.layers
      .filter(l => l.visible)
      .flatMap(l => l.elements);

    let closestPoint = null;
    let minDistance = tolerance;

    for (const el of allElements) {
      if (el.type === 'line' || el.type === 'dimension') {
        const [x1, y1, x2, y2] = el.points;
        const d1 = Math.hypot(pos.x - x1, pos.y - y1);
        if (d1 < minDistance) {
          minDistance = d1;
          closestPoint = { x: x1, y: y1 };
        }
        const d2 = Math.hypot(pos.x - x2, pos.y - y2);
        if (d2 < minDistance) {
          minDistance = d2;
          closestPoint = { x: x2, y: y2 };
        }
      } else if (el.type === 'rectangle') {
        const corners = [
          { x: el.x, y: el.y },
          { x: el.x + el.width, y: el.y },
          { x: el.x, y: el.y + el.height },
          { x: el.x + el.width, y: el.y + el.height }
        ];
        for (const c of corners) {
          const d = Math.hypot(pos.x - c.x, pos.y - c.y);
          if (d < minDistance) {
            minDistance = d;
            closestPoint = c;
          }
        }
      }
    }

    return closestPoint ? closestPoint : pos;
  };

  // Zoom com limites entre 0.2x (20%) e 5.0x (500%)
  const MIN_SCALE = 0.2;
  const MAX_SCALE = 5.0;
  const SCALE_STEP = 1.15;

  const handleZoomIn = () => {
    setStageScale(prevScale => {
      const newScale = Math.min(MAX_SCALE, prevScale * SCALE_STEP);
      // Zoom centralizado na tela
      const center = { x: stageSize.width / 2, y: stageSize.height / 2 };
      setStagePos(prevPos => ({
        x: center.x - (center.x - prevPos.x) * (newScale / prevScale),
        y: center.y - (center.y - prevPos.y) * (newScale / prevScale)
      }));
      return newScale;
    });
  };

  const handleZoomOut = () => {
    setStageScale(prevScale => {
      const newScale = Math.max(MIN_SCALE, prevScale / SCALE_STEP);
      const center = { x: stageSize.width / 2, y: stageSize.height / 2 };
      setStagePos(prevPos => ({
        x: center.x - (center.x - prevPos.x) * (newScale / prevScale),
        y: center.y - (center.y - prevPos.y) * (newScale / prevScale)
      }));
      return newScale;
    });
  };

  const handleZoomReset = () => {
    setStageScale(1);
    setStagePos({ x: 0, y: 0 });
  };

  const [selectedTechnicalPointType, setSelectedTechnicalPointType] = useState('outlet_mid');
  const [calcoOpacity, setCalcoOpacity] = useState(0.45);
  const [calcoLocked, setCalcoLocked] = useState(true);
  const [calcoVisible, setCalcoVisible] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [sidebarTab, setSidebarTab] = useState('properties'); // 'properties' | 'layers'
  const [showCatalogDrawer, setShowCatalogDrawer] = useState(false);
  const [showMobileProperties, setShowMobileProperties] = useState(false);
  const [showMobileLayersModal, setShowMobileLayersModal] = useState(false);

  // Helper para obter elementos da página e camada ativos
  const activePage = pages.find(p => p.id === activePageId) || pages[0];
  const activeLayer = activePage?.layers.find(l => l.id === activeLayerId) || activePage?.layers[0];
  const elements = activeLayer?.elements || [];
  const backgroundImage = activePage?.backgroundImage || null;
  const bgImageElement = activePage?.bgImageElement || null;
  const [bgSelected, setBgSelected] = useState(false);

  // Função para definir imagem de fundo na página atual
  const setBackgroundImage = (imgData) => {
    const currentPageId = activePageIdRef.current;
    setPages(prev => prev.map(page =>
      page.id === currentPageId ? { ...page, backgroundImage: imgData } : page
    ));
  };

  // Função para definir elemento de imagem na página atual
  const setBgImageElement = (imgEl) => {
    const currentPageId = activePageIdRef.current;
    setPages(prev => prev.map(page =>
      page.id === currentPageId ? { ...page, bgImageElement: imgEl } : page
    ));
  };

  // Funções para manipular páginas
  const addPage = () => {
    const newPageId = `page-${Date.now()}`;
    const newLayerId = `layer-${Date.now()}`;
    setPages(prev => [...prev, {
      id: newPageId,
      name: `Página ${prev.length + 1}`,
      layers: [{ id: newLayerId, name: 'Camada 1', visible: true, elements: [] }]
    }]);
    setActivePageId(newPageId);
    setActiveLayerId(newLayerId);
  };

  const deletePage = (pageId) => {
    if (pages.length <= 1) return;
    const newPages = pages.filter(p => p.id !== pageId);
    setPages(newPages);
    if (activePageId === pageId) {
      setActivePageId(newPages[0].id);
      setActiveLayerId(newPages[0].layers[0].id);
    }
  };

  // Funções para manipular camadas
  const addLayer = () => {
    const newLayerId = `layer-${Date.now()}`;
    const currentPageId = activePageIdRef.current;
    setPages(prev => prev.map(page => {
      if (page.id !== currentPageId) return page;
      const newLayer = { id: newLayerId, name: `Camada ${page.layers.length + 1}`, visible: true, elements: [] };
      return { ...page, layers: [...page.layers, newLayer] };
    }));
    setActiveLayerId(newLayerId);
  };

  const deleteLayer = (layerId) => {
    const page = activePage;
    if (page.layers.length <= 1) return;
    const newLayers = page.layers.filter(l => l.id !== layerId);
    const currentPageId = activePageIdRef.current;
    setPages(prev => prev.map(p => p.id === currentPageId ? { ...p, layers: newLayers } : p));
    if (activeLayerId === layerId) {
      setActiveLayerId(newLayers[0].id);
    }
  };

  const toggleLayerVisibility = (layerId) => {
    const currentPageId = activePageIdRef.current;
    setPages(prev => prev.map(page => {
      if (page.id !== currentPageId) return page;
      return {
        ...page,
        layers: page.layers.map(layer =>
          layer.id === layerId ? { ...layer, visible: !layer.visible } : layer
        )
      };
    }));
  };

  const updateLayerName = (layerId, newName) => {
    const currentPageId = activePageIdRef.current;
    setPages(prev => prev.map(page => {
      if (page.id !== currentPageId) return page;
      return {
        ...page,
        layers: page.layers.map(layer =>
          layer.id === layerId ? { ...layer, name: newName } : layer
        )
      };
    }));
  };

  const updatePageName = (pageId, newName) => {
    setPages(prev => prev.map(page =>
      page.id === pageId ? { ...page, name: newName } : page
    ));
  };

  // Funções para manipular elementos na camada ativa
  const setElements = (newElementsOrCallback) => {
    const currentPageId = activePageIdRef.current;
    const currentLayerId = activeLayerIdRef.current;
    setPages(prevPages => {
      const pageIndex = prevPages.findIndex(p => p.id === currentPageId);
      if (pageIndex === -1) return prevPages;

      const page = prevPages[pageIndex];
      const layerIndex = page.layers.findIndex(l => l.id === currentLayerId);
      if (layerIndex === -1) return prevPages;

      const layer = page.layers[layerIndex];

      const isFunction = typeof newElementsOrCallback === 'function';
      const newElements = isFunction
        ? newElementsOrCallback(layer.elements)
        : newElementsOrCallback;

      const newLayers = [...page.layers];
      newLayers[layerIndex] = { ...layer, elements: newElements };

      const newPages = [...prevPages];
      newPages[pageIndex] = { ...page, layers: newLayers };

      return newPages;
    });
  };

  const addElement = (element) => {
    const currentPageId = activePageIdRef.current;
    const currentLayerId = activeLayerIdRef.current;
    setPages(prev => prev.map(page => {
      if (page.id !== currentPageId) return page;
      return {
        ...page,
        layers: page.layers.map(layer =>
          layer.id === currentLayerId ? { ...layer, elements: [...layer.elements, element] } : layer
        )
      };
    }));
  };

  const updateElement = (elementId, updates) => {
    const currentPageId = activePageIdRef.current;
    const currentLayerId = activeLayerIdRef.current;
    setPages(prev => prev.map(page => {
      if (page.id !== currentPageId) return page;
      return {
        ...page,
        layers: page.layers.map(layer =>
          layer.id === currentLayerId
            ? { ...layer, elements: layer.elements.map(el => el.id === elementId ? { ...el, ...updates } : el) }
            : layer
        )
      };
    }));
  };

  const deleteElement = (elementId) => {
    const currentPageId = activePageIdRef.current;
    // Apaga o elemento em QUALQUER camada da página ativa
    setPages(prev => prev.map(page => {
      if (page.id !== currentPageId) return page;
      return {
        ...page,
        layers: page.layers.map(layer => ({
          ...layer,
          elements: layer.elements.filter(el => el.id !== elementId)
        }))
      };
    }));
  };

  const getNextId = (prefix) => {
    const id = idCounterRef.current++;
    return `${prefix}-${id}`;
  };

  // Função para carregar imagem de fundo
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const img = new window.Image();
      img.src = reader.result;
      img.onload = () => {
        setBackgroundImage(reader.result);
        setBgImageElement(img);
        // Definir tamanho inicial baseado na imagem
        setBgSelected(true);
      };
    };
    reader.readAsDataURL(file);

    // Reset input para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Função para remover imagem de fundo
  const handleRemoveBackgroundImage = () => {
    setBackgroundImage(null);
    setBgImageElement(null);
    setBgSelected(false);
    // Reset input para permitir selecionar novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Efeito para atualizar o Transformer quando bgSelected mudar
  useEffect(() => {
    if (bgSelected && transformerRef.current && bgImageGroupRef.current) {
      transformerRef.current.nodes([bgImageGroupRef.current]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [bgSelected]);

  // Efeito para restaurar posição/tamanho da imagem de fundo após carregar
  useEffect(() => {
    if (!bgImageElement || !bgImageGroupRef.current) {
      return;
    }

    // PC-3 FIX: backgroundTransform agora está dentro de cada página
    const tf = activePage?.backgroundTransform;
    if (!tf) return;

    const group = bgImageGroupRef.current;

    // Aplicar transform com pequeno delay para garantir que o componente esteja pronto
    setTimeout(() => {
      if (tf.x !== undefined) group.x(tf.x);
      if (tf.y !== undefined) group.y(tf.y);
      if (tf.width) group.width(tf.width);
      if (tf.height) group.height(tf.height);
      if (tf.scaleX) group.scaleX(tf.scaleX);
      if (tf.scaleY) group.scaleY(tf.scaleY);
      if (tf.rotation) group.rotation(tf.rotation);
      group.getLayer()?.batchDraw();
    }, 100);
  }, [bgImageElement, project]);

  // Carregar projeto
  useEffect(() => {
    const loadProject = async () => {
      try {
        const response = await sketchService.getProjectById(projectId);
        const projectData = response.data;
        setProject(projectData);

        // 1) Prioriza elementos do canvas_data (estado salvo do editor)
        if (projectData.canvas_data?.pages?.length) {
          // Nova estrutura com páginas e camadas
          setPages(projectData.canvas_data.pages);
          setActivePageId(projectData.canvas_data.pages[0].id);
          setActiveLayerId(projectData.canvas_data.pages[0].layers[0].id);

          // Carregar imagem de fundo se existir (primeira página)
          const firstPage = projectData.canvas_data.pages[0];
          if (firstPage?.backgroundImage) {
            const img = new window.Image();
            img.src = firstPage.backgroundImage;
            img.onload = () => {
              setBackgroundImage(firstPage.backgroundImage);
              setBgImageElement(img);
            };
          }

          // Ajustar contador de IDs
          let maxIdNum = 0;
          projectData.canvas_data.pages.forEach(page => {
            page.layers.forEach(layer => {
              layer.elements.forEach(el => {
                if (!el.id || typeof el.id !== 'string') return;
                const match = el.id.match(/-(\d+)$/);
                if (!match) return;
                const num = parseInt(match[1], 10);
                if (!Number.isNaN(num)) maxIdNum = Math.max(maxIdNum, num);
              });
            });
          });
          idCounterRef.current = maxIdNum + 1;

          return;
        }

        // Compatibilidade: se tem elementos no formato antigo (array simples)
        if (projectData.canvas_data?.elements?.length) {
          const loaded = projectData.canvas_data.elements;
          // Converter para nova estrutura de páginas/camadas
          setPages([{
            id: 'page-1',
            name: 'Página 1',
            layers: [{ id: 'layer-1', name: 'Camada 1', visible: true, elements: loaded }]
          }]);
          setActivePageId('page-1');
          setActiveLayerId('layer-1');

          // Carregar imagem de fundo se existir
          if (projectData.canvas_data?.backgroundImage) {
            const img = new window.Image();
            img.src = projectData.canvas_data.backgroundImage;
            img.onload = () => {
              setBackgroundImage(projectData.canvas_data.backgroundImage);
              setBgImageElement(img);
            };
          }

          // Ajustar contador de IDs para evitar duplicados
          const maxIdNum = loaded.reduce((max, el) => {
            if (!el.id || typeof el.id !== 'string') return max;
            const match = el.id.match(/-(\d+)$/);
            if (!match) return max;
            const num = parseInt(match[1], 10);
            return Number.isNaN(num) ? max : Math.max(max, num);
          }, 0);
          idCounterRef.current = maxIdNum + 1;

          return;
        }

        // 2) Se não houver canvas_data, tenta reconstruir a partir de sketch_elements do backend
        // PC-5 FIX: Reconstruir estrutura de páginas/camadas se pageId/layerId existirem
        if (Array.isArray(projectData.elements) && projectData.elements.length > 0) {
          const restoredElements = projectData.elements.map((row) => {
            const base = row.element_data || {};
            const measurements = row.measurements || {};

            if (row.element_type === 'rectangle') {
              return {
                id: base.id || `rect-${row.id}`,
                type: 'rectangle',
                x: base.x ?? 0,
                y: base.y ?? 0,
                width: base.width ?? 50,
                height: base.height ?? 50,
                measurements: {
                  width: measurements.width ?? base.width ?? 50,
                  height: measurements.height ?? base.height ?? 50
                },
                pageId: row.pageId || null,
                layerId: row.layerId || null
              };
            }

            if (row.element_type === 'line') {
              return {
                id: base.id || `line-${row.id}`,
                type: 'line',
                points: base.points || [0, 0, 100, 0],
                measurements: {
                  length: measurements.length
                    ?? (Array.isArray(base.points) && base.points.length === 4
                      ? Math.sqrt(
                          Math.pow(base.points[2] - base.points[0], 2) +
                          Math.pow(base.points[3] - base.points[1], 2)
                        )
                      : 100)
                },
                pageId: row.pageId || null,
                layerId: row.layerId || null
              };
            }

            if (row.element_type === 'dimension') {
              return {
                id: base.id || `dim-${row.id}`,
                type: 'dimension',
                points: base.points || [0, 0, 100, 0],
                measurements: measurements || base.measurements || { length: 100 },
                pageId: row.pageId || null,
                layerId: row.layerId || null
              };
            }

            if (row.element_type === 'technical_point') {
              return {
                id: base.id || `techpoint-${row.id}`,
                type: 'technical_point',
                pointType: base.pointType || 'outlet_mid',
                pointCategory: base.pointCategory || 'electrical',
                x: base.x ?? 0,
                y: base.y ?? 0,
                heightFromFloorCm: base.heightFromFloorCm ?? 110,
                notes: base.notes || '',
                rotation: base.rotation || 0,
                pageId: row.pageId || null,
                layerId: row.layerId || null
              };
            }

            if (row.element_type === 'text') {
              return {
                id: base.id || `text-${row.id}`,
                type: 'text',
                x: base.x ?? 0,
                y: base.y ?? 0,
                text: base.text || '',
                fontSize: base.fontSize || 16,
                color: base.color || '#000000',
                pageId: row.pageId || null,
                layerId: row.layerId || null
              };
            }

            if (row.element_type === 'freehand') {
              return {
                id: base.id || `freehand-${row.id}`,
                type: 'freehand',
                points: base.points || [],
                color: base.color || '#000000',
                strokeWidth: base.strokeWidth || 2,
                pageId: row.pageId || null,
                layerId: row.layerId || null
              };
            }

            if (row.element_type === 'appliance') {
              return {
                id: base.id || `appliance-${row.id}`,
                type: 'appliance',
                svgId: base.svgId || 'cooktop_4b',
                name: base.name || 'Eletro',
                x: base.x ?? 100,
                y: base.y ?? 100,
                width: base.width || 60,
                height: base.height || 60,
                rotation: base.rotation || 0,
                pageId: row.pageId || null,
                layerId: row.layerId || null
              };
            }

            // Caso apareça um tipo desconhecido, ignora
            return null;
          }).filter(Boolean);

          // Verificar se elementos têm pageId/layerId para reconstruir estrutura
          const hasPageLayerInfo = restoredElements.some(el => el.pageId || el.layerId);

          if (hasPageLayerInfo) {
            // Reconstruir estrutura de páginas/camadas
            const pagesMap = new Map();
            const defaultPageId = 'page-1';
            const defaultLayerId = 'layer-1';

            restoredElements.forEach(el => {
              const pageId = el.pageId || defaultPageId;
              const layerId = el.layerId || defaultLayerId;

              if (!pagesMap.has(pageId)) {
                pagesMap.set(pageId, {
                  id: pageId,
                  name: `Página ${pagesMap.size + 1}`,
                  layers: [{
                    id: layerId,
                    name: 'Camada 1',
                    visible: true,
                    elements: []
                  }]
                });
              }

              const page = pagesMap.get(pageId);
              let layer = page.layers.find(l => l.id === layerId);
              if (!layer) {
                layer = {
                  id: layerId,
                  name: `Camada ${page.layers.length + 1}`,
                  visible: true,
                  elements: []
                };
                page.layers.push(layer);
              }

              // Remover pageId/layerId do elemento antes de adicionar à camada
              const { pageId: _, layerId: __, ...elementWithoutIds } = el;
              layer.elements.push(elementWithoutIds);
            });

            const reconstructedPages = Array.from(pagesMap.values());
            setPages(reconstructedPages);
            setActivePageId(reconstructedPages[0].id);
            setActiveLayerId(reconstructedPages[0].layers[0].id);
          } else {
            // Compatibilidade: todos os elementos para a camada padrão
            setPages([{
              id: 'page-1',
              name: 'Página 1',
              layers: [{ id: 'layer-1', name: 'Camada 1', visible: true, elements: restoredElements }]
            }]);
            setActivePageId('page-1');
            setActiveLayerId('layer-1');
          }

          const maxIdNum = restoredElements.reduce((max, el) => {
            if (!el.id || typeof el.id !== 'string') return max;
            const match = el.id.match(/-(\d+)$/);
            if (!match) return max;
            const num = parseInt(match[1], 10);
            return Number.isNaN(num) ? max : Math.max(max, num);
          }, 0);
          idCounterRef.current = maxIdNum + 1;
        }
      } catch (error) {
        console.error('Erro ao carregar:', error);
      }
    };

    loadProject();
  }, [projectId]);

  // Inserir item do catálogo (por clique ou soltando no canvas)
  const insertApplianceItem = (item, worldCoordinates = null) => {
    let targetX = worldCoordinates?.x;
    let targetY = worldCoordinates?.y;

    if (targetX === undefined || targetY === undefined) {
      // Se não foram fornecidas coordenadas de drag, insere no centro visual do canvas
      const centerScreen = { x: stageSize.width / 2, y: stageSize.height / 2 };
      targetX = (centerScreen.x - stagePos.x) / stageScale;
      targetY = (centerScreen.y - stagePos.y) / stageScale;
    }

    const newAppliance = {
      id: getNextId('appliance'),
      type: 'appliance',
      svgId: item.svgId || item.id,
      name: item.name,
      x: Math.round(targetX),
      y: Math.round(targetY),
      width: item.width || item.defaultWidth || 60,
      height: item.height || item.defaultHeight || 60,
      rotation: 0
    };

    setElements(prev => [...prev, newAppliance]);
    setSelectedId(newAppliance.id);
    setTool('select');
    setShowCatalogDrawer(false);
  };

  // Handlers para HTML5 Drag and Drop sobre o container do canvas
  const handleCanvasDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleCanvasDrop = (e) => {
    e.preventDefault();
    const rawData = e.dataTransfer.getData('application/json');
    if (!rawData) return;

    try {
      const itemData = JSON.parse(rawData);
      if (itemData.type !== 'appliance') return;

      const container = canvasContainerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;

      // Converter screenX/screenY para worldX/worldY considerando pan e zoom
      const worldX = (screenX - stagePos.x) / stageScale;
      const worldY = (screenY - stagePos.y) / stageScale;

      insertApplianceItem(itemData, { x: worldX, y: worldY });
    } catch (err) {
      console.error('Erro ao processar drop no canvas:', err);
    }
  };

  // Girar elemento selecionado em 90 graus (atalho de teclado R ou botão)
  const rotateSelectedElement = (angleStep = 90) => {
    if (!selectedId) return;
    setElements(prev =>
      prev.map(el => {
        if (el.id !== selectedId) return el;
        const currentRot = el.rotation || 0;
        return {
          ...el,
          rotation: (currentRot + angleStep) % 360
        };
      })
    );
  };

  // Inverter / Espelhar elemento selecionado (ex: arco de porta)
  const flipSelectedElement = () => {
    if (!selectedId) return;
    setElements(prev =>
      prev.map(el => {
        if (el.id !== selectedId) return el;
        return {
          ...el,
          flipped: !el.flipped
        };
      })
    );
  };

  // Atalhos de teclado (Escape, Delete, R para girar, e Espaço para Pan)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignorar se estiver digitando em inputs ou textareas
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;

      if (e.code === 'Space' && !isSpacePressedRef.current) {
        e.preventDefault();
        isSpacePressedRef.current = true;
        setIsSpacePressed(true);
      }
      if (e.key === 'Escape') setSelectedId(null);
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        setElements(prev => prev.filter(el => el.id !== selectedId));
        setSelectedId(null);
      }
      if ((e.key === 'r' || e.key === 'R') && selectedId) {
        rotateSelectedElement();
      }
      // Atalhos Ctrl+Z (Undo) e Ctrl+Y / Ctrl+Shift+Z (Redo)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault();
        handleRedo();
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space') {
        isSpacePressedRef.current = false;
        setIsSpacePressed(false);
        setIsPanning(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [selectedId]);

  // Handler de Zoom com a Roda do Mouse (Focal Zoom em direção ao cursor)
  const handleWheel = (e) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;

    const oldScale = stageScale;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    // Ponto no mundo virtual sob o cursor
    const mousePointTo = {
      x: (pointer.x - stagePos.x) / oldScale,
      y: (pointer.y - stagePos.y) / oldScale,
    };

    // Rolar para cima aproxima, para baixo afasta
    const direction = e.evt.deltaY < 0 ? 1 : -1;
    const factor = direction > 0 ? SCALE_STEP : 1 / SCALE_STEP;
    let newScale = oldScale * factor;
    newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));

    const newPos = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };

    setStageScale(newScale);
    setStagePos(newPos);
  };

  const handleStageMouseDown = (e) => {
    // Suporte a Pan: Espaço pressionado, botão do meio (button === 1), ou botão direito
    if (isSpacePressedRef.current || e.evt.button === 1) {
      setIsPanning(true);
      panStartRef.current = {
        x: e.evt.clientX - stagePos.x,
        y: e.evt.clientY - stagePos.y
      };
      return;
    }

    const target = e.target;
    const stage = target?.getStage ? target.getStage() : e.target.getStage();
    const pos = getPointerWorldPosition(stage);

    // Verificar se há imagem de fundo e clique está na área da imagem
    const isClickOnBackground = bgImageElement &&
      pos.x >= 0 && pos.x <= 1200 &&
      pos.y >= 0 && pos.y <= 800;

    // Clicou no Stage diretamente ou na área da imagem (mas não em elemento desenhado)
    const clickedOnEmpty = !target || target === stage;

    // Modo Apagar (Borracha / Eraser: 1 clique/toque no elemento apaga imediatamente, independente da camada)
    if (tool === 'eraser') {
      const { x, y } = pos;
      const HIT_TOLERANCE = 20;
      let hitElement = null;

      // Percorre todos os elementos visíveis de QUALQUER camada da página
      const allVisibleElements = activePage.layers
        .filter(layer => layer.visible)
        .flatMap(layer => layer.elements);

      for (let i = allVisibleElements.length - 1; i >= 0; i--) {
        const el = allVisibleElements[i];

        if (el.type === 'rectangle') {
          if (x >= el.x && x <= el.x + el.width && y >= el.y && y <= el.y + el.height) {
            hitElement = el;
            break;
          }
        } else if (el.type === 'line' || el.type === 'dimension') {
          const [x1, y1, x2, y2] = el.points;
          const dist = distancePointToSegment(x, y, x1, y1, x2, y2);
          if (dist <= 30) {
            hitElement = el;
            break;
          }
        } else if (el.type === 'freehand') {
          const points = el.points;
          for (let j = 0; j < points.length - 2; j += 2) {
            const px = points[j];
            const py = points[j + 1];
            const dist = Math.hypot(x - px, y - py);
            if (dist <= HIT_TOLERANCE * 2) {
              hitElement = el;
              break;
            }
          }
          if (hitElement) break;
        } else if (el.type === 'technical_point') {
          const dist = Math.hypot(x - el.x, y - el.y);
          if (dist <= 40) {
            hitElement = el;
            break;
          }
        } else if (el.type === 'text') {
          const fontSize = el.fontSize || 16;
          const textWidth = el.text.length * fontSize * 0.6;
          const textHeight = fontSize;
          if (x >= el.x - 5 && x <= el.x + textWidth + 5 && y >= el.y - textHeight - 5 && y <= el.y + textHeight + 5) {
            hitElement = el;
            break;
          }
        } else if (el.type === 'circle') {
          const r = el.radius || 30;
          if (Math.hypot(x - el.x, y - el.y) <= r + 15) {
            hitElement = el;
            break;
          }
        } else if (el.type === 'door_arc') {
          const w = el.width || 80;
          if (Math.hypot(x - el.x, y - el.y) <= w + 20) {
            hitElement = el;
            break;
          }
        } else if (el.type === 'window') {
          const w = el.width || 100;
          if (Math.hypot(x - el.x, y - el.y) <= w + 20) {
            hitElement = el;
            break;
          }
        } else if (el.type === 'appliance') {
          const halfW = (el.width || 60) / 2;
          const halfH = (el.height || 60) / 2;
          if (x >= el.x - halfW && x <= el.x + halfW && y >= el.y - halfH && y <= el.y + halfH) {
            hitElement = el;
            break;
          }
        }
      }

      if (hitElement) {
        deleteElement(hitElement.id);
        if (selectedId === hitElement.id) setSelectedId(null);
      }
      return;
    }

    // Modo texto: abrir input (em coordenadas de tela da viewport e guardando as do mundo)
    if (tool === 'text') {
      const screenPos = stage.getPointerPosition();
      setTextInputPos({ x: screenPos.x, y: screenPos.y, worldX: pos.x, worldY: pos.y });
      setShowTextInput(true);
      setTextInputValue('');
      setTimeout(() => textInputRef.current?.focus(), 100);
      return;
    }

    // Modo Ponto Técnico (1 clique posiciona o símbolo)
    if (tool === 'technical_point') {
      const def = TECHNICAL_POINTS_CATALOG.find(p => p.id === selectedTechnicalPointType) || TECHNICAL_POINTS_CATALOG[1];
      const newPoint = {
        id: getNextId('techpoint'),
        type: 'technical_point',
        pointCategory: def.category,
        pointType: def.id,
        x: Math.round(pos.x),
        y: Math.round(pos.y),
        heightFromFloorCm: def.defaultHeight,
        notes: '',
        rotation: 0
      };
      setElements(prev => [...prev, newPoint]);
      setSelectedId(newPoint.id);
      setTool('select');
      return;
    }

    // Modo desenhar:
    // Para caneta e linha, NUNCA bloqueia (permite iniciar em cima de outros traços para fechar quadrados)
    const isDrawingTool = ['pen', 'line', 'dimension', 'rectangle'].includes(tool);
    const canDraw = isDrawingTool || clickedOnEmpty || (isClickOnBackground && tool !== 'select');
    if (!canDraw) return;

    // Se estiver em modo linha ou caneta reta, aplica snap magnético para grudar nas pontas
    const drawPos = (tool === 'pen' && penMode === 'line') || tool === 'line' || tool === 'dimension'
      ? getSnappedPoint(pos, 20)
      : pos;

    // Modo caneta
    if (tool === 'pen') {
      setIsDrawing(true);
      if (penMode === 'freehand') {
        setFreehandPoints([drawPos.x, drawPos.y]);
      } else {
        // Modo linha reta: inicializa com currentX/Y para garantir preview e fechamento imediato
        setStartPos({ x: drawPos.x, y: drawPos.y, currentX: drawPos.x, currentY: drawPos.y });
      }
      return;
    }

    setIsDrawing(true);
    setStartPos({ x: drawPos.x, y: drawPos.y, currentX: drawPos.x, currentY: drawPos.y });
  };

  const handleStageMouseMove = (e) => {
    // Processamento de Pan
    if (isPanning && panStartRef.current) {
      setStagePos({
        x: e.evt.clientX - panStartRef.current.x,
        y: e.evt.clientY - panStartRef.current.y
      });
      return;
    }

    const target = e.target;
    const stage = target?.getStage ? target.getStage() : null;
    if (!stage) return;

    const rawPos = getPointerWorldPosition(stage);
    if (!rawPos) return;

    // Se estiver em modo linha ou caneta reta, aplica snap suave nas pontas
    const pos = (tool === 'pen' && penMode === 'line') || tool === 'line' || tool === 'dimension'
      ? getSnappedPoint(rawPos, 20)
      : rawPos;

    // Modo caneta
    if (tool === 'pen' && isDrawing) {
      if (penMode === 'freehand') {
        setFreehandPoints(prev => [...prev, pos.x, pos.y]);
      } else {
        // Modo linha reta: atualiza posição atual para preview
        setStartPos(prev => prev ? { ...prev, currentX: pos.x, currentY: pos.y } : null);
      }
      return;
    }

    if (!isDrawing || !startPos) return;

    // Apenas atualiza a posição atual para renderizar preview
    setStartPos(prev => prev ? { ...prev, currentX: pos.x, currentY: pos.y } : null);
  };

  const handleStageMouseUp = (e) => {
    if (isPanning) {
      setIsPanning(false);
      panStartRef.current = null;
      return;
    }

    const target = e.target;
    const stage = target?.getStage ? target.getStage() : null;
    if (!stage) return;

    const rawPos = getPointerWorldPosition(stage);
    if (!rawPos) return;

    // Se estiver em modo linha ou caneta reta, aplica snap suave nas pontas
    const pos = (tool === 'pen' && penMode === 'line') || tool === 'line' || tool === 'dimension'
      ? getSnappedPoint(rawPos, 20)
      : rawPos;

    // Modo caneta
    if (tool === 'pen' && isDrawing) {
      if (penMode === 'freehand' && freehandPoints.length > 4) {
        const newFreehand = {
          id: getNextId('freehand'),
          type: 'freehand',
          points: freehandPoints,
          color: brushColor,
          strokeWidth: brushSize
        };
        setElements(prev => [...prev, newFreehand]);
        setFreehandPoints([]);
      } else if (penMode === 'line' && startPos) {
        const endX = startPos.currentX !== undefined ? startPos.currentX : pos.x;
        const endY = startPos.currentY !== undefined ? startPos.currentY : pos.y;
        const dist = Math.hypot(endX - startPos.x, endY - startPos.y);

        // Cria a linha se houver qualquer traço visível (>= 5px)
        if (dist >= 5) {
          const newLine = {
            id: getNextId('line'),
            type: 'line',
            points: [startPos.x, startPos.y, endX, endY],
            color: brushColor,
            strokeWidth: brushSize
          };
          setElements(prev => [...prev, newLine]);
        }
      }
      setIsDrawing(false);
      setStartPos(null);
      return;
    }

    if (!isDrawing || !startPos) {
      if (tool === 'pen') {
        setIsDrawing(false);
        setFreehandPoints([]);
      }
      return;
    }

    setIsDrawing(false);

    if (tool === 'rectangle') {
      const width = Math.abs(pos.x - startPos.x);
      const height = Math.abs(pos.y - startPos.y);

      if (width > 10 && height > 10) {
        const newRect = {
          id: getNextId('rect'),
          type: 'rectangle',
          x: Math.min(startPos.x, pos.x),
          y: Math.min(startPos.y, pos.y),
          width,
          height,
          measurements: { width, height }
        };

        setElements(prev => [...prev, newRect]);
      }
    } else if (tool === 'circle') {
      const dist = Math.hypot(pos.x - startPos.x, pos.y - startPos.y);
      const radius = dist > 10 ? Math.round(dist) : 30; // default 30cm (diâmetro 60cm) se apenas tocou
      const newCircle = {
        id: getNextId('circle'),
        type: 'circle',
        x: Math.round(startPos.x),
        y: Math.round(startPos.y),
        radius,
        measurements: {
          diameter: radius * 2
        }
      };
      setElements(prev => [...prev, newCircle]);
      setSelectedId(newCircle.id);
    } else if (tool === 'door_arc') {
      const dist = Math.hypot(pos.x - startPos.x, pos.y - startPos.y);
      const width = dist > 15 ? Math.round(dist) : 80; // default 80cm se apenas tocou
      const newDoor = {
        id: getNextId('door'),
        type: 'door_arc',
        x: Math.round(startPos.x),
        y: Math.round(startPos.y),
        width,
        rotation: 0,
        flipped: false
      };
      setElements(prev => [...prev, newDoor]);
      setSelectedId(newDoor.id);
    } else if (tool === 'window') {
      const dist = Math.hypot(pos.x - startPos.x, pos.y - startPos.y);
      const width = dist > 15 ? Math.round(dist) : 100; // default 100cm
      const angleRad = Math.atan2(pos.y - startPos.y, pos.x - startPos.x);
      let angleDeg = Math.round((angleRad * 180) / Math.PI / 15) * 15;
      if (dist <= 15) angleDeg = 0;
      const newWindow = {
        id: getNextId('window'),
        type: 'window',
        x: Math.round(startPos.x),
        y: Math.round(startPos.y),
        width,
        height: 15,
        alturaCm: 120,
        peitorilCm: 100,
        rotation: (angleDeg + 360) % 360
      };
      setElements(prev => [...prev, newWindow]);
      setSelectedId(newWindow.id);
    } else if (tool === 'line' || tool === 'dimension') {
      const dx = pos.x - startPos.x;
      const dy = pos.y - startPos.y;
      const length = Math.sqrt(dx * dx + dy * dy);

      if (length > 10) {
        if (tool === 'dimension') {
          const newDim = {
            id: getNextId('dim'),
            type: 'dimension',
            points: [Math.round(startPos.x), Math.round(startPos.y), Math.round(pos.x), Math.round(pos.y)],
            measurements: { length: Math.round(length) }
          };
          setElements(prev => [...prev, newDim]);
        } else {
          const newLine = {
            id: getNextId('line'),
            type: 'line',
            points: [startPos.x, startPos.y, pos.x, pos.y],
            measurements: { length }
          };
          setElements(prev => [...prev, newLine]);
        }
      }
    }

    setStartPos(null);

    // Ferramentas de desenho contínuo (linha, cota, retângulo, caneta, borracha):
    // Permanecem ativas para que o usuário possa desenhar múltiplos elementos sucessivamente
    // sem ter que reativar a ferramenta a cada traço.
    // O usuário só volta para 'select' se clicar explicitamente no botão "Mover" ou selecionar um elemento existente.
    const continuousTools = ['line', 'dimension', 'rectangle', 'circle', 'door_arc', 'window', 'pen', 'eraser'];
    if (!continuousTools.includes(tool)) {
      setTool('select');
    }
  };

  const handleTextSubmit = () => {
    const valueToSubmit = textInputValue.trim() || textInputValueRef.current.trim();
    console.log('[SketchEditor] handleTextSubmit called, state value:', textInputValue, 'ref value:', textInputValueRef.current, 'using:', valueToSubmit);
    if (valueToSubmit) {
      const newText = {
        id: getNextId('text'),
        type: 'text',
        x: textInputPos.worldX ?? textInputPos.x,
        y: textInputPos.worldY ?? textInputPos.y,
        text: valueToSubmit,
        color: brushColor,
        fontSize: 16
      };
      console.log('[SketchEditor] Adding text element:', newText);
      setElements(prev => [...prev, newText]);
    }
    setShowTextInput(false);
    setTextInputValue('');
    textInputValueRef.current = '';
    setTool('select');
  };

  const handleElementClick = (elementId) => {
    // Se o usuário estiver desenhando com caneta, linha, cota ou retângulo,
    // NÃO seleciona o elemento nem muda para a ferramenta 'select'.
    // Isso garante que você possa cruzar, encostar ou fechar linhas e quadrados
    // livremente sem que a tela 'trave' ou mude de ferramenta.
    const drawingTools = ['pen', 'line', 'dimension', 'rectangle', 'eraser'];
    if (drawingTools.includes(tool)) {
      return;
    }

    console.log('[SketchEditor] Selecionando elemento:', elementId);
    setSelectedId(elementId);
    if (tool !== 'select') {
      setTool('select');
    }
  };

  useEffect(() => {
    if (selectedId) {
      console.log('[SketchEditor] Elemento selecionado:', selectedId);
    } else {
      console.log('[SketchEditor] Nenhum elemento selecionado');
    }
  }, [selectedId]);

  const distancePointToSegment = (px, py, x1, y1, x2, y2) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    if (dx === 0 && dy === 0) {
      // segmento degenerado
      return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
    }
    const t = ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy);
    const clampedT = Math.max(0, Math.min(1, t));
    const projX = x1 + clampedT * dx;
    const projY = y1 + clampedT * dy;
    return Math.sqrt((px - projX) ** 2 + (py - projY) ** 2);
  };

  const handleLinePointDrag = (id, pointIndex, e) => {
    const { x, y } = e.target.position();
    setElements(prev =>
      prev.map(el => {
        if (el.id !== id || (el.type !== 'line' && el.type !== 'dimension')) return el;
        const points = [...el.points];
        points[pointIndex * 2] = x;
        points[pointIndex * 2 + 1] = y;

        const [x1, y1, x2, y2] = points;
        const length = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
        const angleRad = Math.atan2(y2 - y1, x2 - x1);
        const angleDeg = (angleRad * 180) / Math.PI;

        return {
          ...el,
          points,
          measurements: {
            ...(el.measurements || {}),
            length,
            angle: angleDeg
          }
        };
      })
    );
  };

  // Redimensionamento dinâmico de círculo arrastando a alça do perímetro (como na linha)
  const handleCircleRadiusDrag = (id, e) => {
    const handlePos = e.target.position();
    const el = elements.find(item => item.id === id);
    if (!el) return;
    const dist = Math.hypot(handlePos.x - el.x, handlePos.y - el.y);
    const newRadius = Math.max(5, Math.round(dist));
    setElements(prev =>
      prev.map(item => {
        if (item.id !== id || item.type !== 'circle') return item;
        return {
          ...item,
          radius: newRadius,
          measurements: {
            ...(item.measurements || {}),
            diameter: newRadius * 2
          }
        };
      })
    );
  };

  // Redimensionamento dinâmico do vão da porta puxando a alça
  const handleDoorWidthDrag = (id, e) => {
    const handlePos = e.target.position();
    const el = elements.find(item => item.id === id);
    if (!el) return;
    const dist = Math.hypot(handlePos.x - el.x, handlePos.y - el.y);
    const newWidth = Math.max(20, Math.round(dist));
    setElements(prev =>
      prev.map(item => {
        if (item.id !== id || item.type !== 'door_arc') return item;
        return {
          ...item,
          width: newWidth
        };
      })
    );
  };

  // Redimensionamento dinâmico do retângulo/módulo pelo canto inferior direito
  const handleRectCornerDrag = (id, e) => {
    const handlePos = e.target.position();
    const el = elements.find(item => item.id === id);
    if (!el) return;
    const newW = Math.max(10, Math.round(handlePos.x - el.x));
    const newH = Math.max(10, Math.round(handlePos.y - el.y));
    setElements(prev =>
      prev.map(item => {
        if (item.id !== id || item.type !== 'rectangle') return item;
        return {
          ...item,
          width: newW,
          height: newH,
          measurements: { width: newW, height: newH }
        };
      })
    );
  };

  const updateMeasurement = (field, value) => {
    if (!selectedId) return;

    const num = parseFloat(value);
    if (isNaN(num)) return;

    setElements(prev =>
      prev.map(el => {
        if (el.id !== selectedId) return el;

        if (el.type === 'rectangle') {
          if (field === 'width' && num > 0) {
            return {
              ...el,
              width: num,
              measurements: { ...(el.measurements || {}), width: num }
            };
          }
          if (field === 'height' && num > 0) {
            return {
              ...el,
              height: num,
              measurements: { ...(el.measurements || {}), height: num }
            };
          }
        } else if (el.type === 'line') {
          const [x1, y1, x2, y2] = el.points;
          const currentLength = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2)) || 1;
          const currentAngleRad = Math.atan2(y2 - y1, x2 - x1);

          if (field === 'length' && num > 0) {
            const newX2 = x1 + num * Math.cos(currentAngleRad);
            const newY2 = y1 + num * Math.sin(currentAngleRad);
            return {
              ...el,
              points: [x1, y1, newX2, newY2],
              measurements: { ...(el.measurements || {}), length: num }
            };
          }

          if (field === 'angle') {
            const angleRad = (num * Math.PI) / 180;
            const length = el.measurements?.length || currentLength;
            const newX2 = x1 + length * Math.cos(angleRad);
            const newY2 = y1 + length * Math.sin(angleRad);
            return {
              ...el,
              points: [x1, y1, newX2, newY2],
              measurements: { ...(el.measurements || {}), length, angle: num }
            };
          }
        }

        return el;
      })
    );
  };

  // Handler unificado para pointer down (mouse/touch)
  const handlePointerDown = (e) => {
    const clientX = e.evt.clientX ?? e.evt.touches?.[0]?.clientX ?? e.evt.changedTouches?.[0]?.clientX ?? 0;
    const clientY = e.evt.clientY ?? e.evt.touches?.[0]?.clientY ?? e.evt.changedTouches?.[0]?.clientY ?? 0;

    // Se estiver com Espaço pressionado ou botão do meio (1), iniciar Pan
    if (isSpacePressedRef.current || e.evt.button === 1) {
      setIsPanning(true);
      panStartRef.current = {
        x: clientX - stagePos.x,
        y: clientY - stagePos.y
      };
      return;
    }

    const target = e.target;
    const stage = target.getStage();
    const pos = getPointerWorldPosition(stage);
    if (!pos) return;

    if (tool === 'select') {
      const { x, y } = pos;
      const HIT_TOLERANCE = 10;
      let hitElement = null;

      const allVisibleElements = activePage.layers
        .filter(layer => layer.visible)
        .flatMap(layer => layer.elements);

      for (let i = allVisibleElements.length - 1; i >= 0; i--) {
        const el = allVisibleElements[i];

        if (el.type === 'rectangle') {
          if (x >= el.x && x <= el.x + el.width && y >= el.y && y <= el.y + el.height) {
            hitElement = el;
            break;
          }
        } else if (el.type === 'line') {
          const [x1, y1, x2, y2] = el.points;
          const dist = distancePointToSegment(x, y, x1, y1, x2, y2);
          if (dist <= HIT_TOLERANCE) {
            hitElement = el;
            break;
          }
        } else if (el.type === 'freehand') {
          const points = el.points;
          for (let j = 0; j < points.length - 2; j += 2) {
            const px = points[j];
            const py = points[j + 1];
            const dist = Math.sqrt((x - px) ** 2 + (y - py) ** 2);
            if (dist <= HIT_TOLERANCE * 2) {
              hitElement = el;
              break;
            }
          }
          if (hitElement) break;
        } else if (el.type === 'technical_point') {
          const dist = Math.sqrt((x - el.x) ** 2 + (y - el.y) ** 2);
          if (dist <= 30) {
            hitElement = el;
            break;
          }
        } else if (el.type === 'dimension') {
          const [x1, y1, x2, y2] = el.points;
          const dist = distancePointToSegment(x, y, x1, y1, x2, y2);
          if (dist <= 20) {
            hitElement = el;
            break;
          }
        } else if (el.type === 'text') {
          const fontSize = el.fontSize || 16;
          const textWidth = el.text.length * fontSize * 0.6;
          const textHeight = fontSize;
          if (x >= el.x && x <= el.x + textWidth && y >= el.y - textHeight && y <= el.y + textHeight) {
            hitElement = el;
            break;
          }
        } else if (el.type === 'circle') {
          const r = el.radius || 30;
          const dist = Math.hypot(x - el.x, y - el.y);
          if (dist <= r + 15) {
            hitElement = el;
            break;
          }
        } else if (el.type === 'door_arc') {
          const w = el.width || 80;
          const dist = Math.hypot(x - el.x, y - el.y);
          if (dist <= w + 20) {
            hitElement = el;
            break;
          }
        } else if (el.type === 'window') {
          const w = el.width || 100;
          const h = el.height || 15;
          const rad = -((el.rotation || 0) * Math.PI) / 180;
          const cos = Math.cos(rad);
          const sin = Math.sin(rad);
          const dx = x - el.x;
          const dy = y - el.y;
          const localX = dx * cos - dy * sin;
          const localY = dx * sin + dy * cos;
          if (localX >= -15 && localX <= w + 15 && localY >= -15 && localY <= h + 15) {
            hitElement = el;
            break;
          }
        } else if (el.type === 'appliance') {
          const halfW = (el.width || 60) / 2;
          const halfH = (el.height || 60) / 2;
          if (x >= el.x - halfW - 15 && x <= el.x + halfW + 15 && y >= el.y - halfH - 15 && y <= el.y + halfH + 15) {
            hitElement = el;
            break;
          }
        }
      }

      if (hitElement) {
        handleElementClick(hitElement.id);
        setDraggingId(hitElement.id);
        dragStartRef.current = { pos, element: hitElement };
      } else {
        if (bgImageElement && pos.x >= 0 && pos.x <= stageSize.width && pos.y >= 0 && pos.y <= stageSize.height) {
          setBgSelected(true);
        } else {
          setSelectedId(null);
          setBgSelected(false);
          setDraggingId(null);
          dragStartRef.current = null;
        }
      }
    } else {
      handleStageMouseDown(e);
    }
  };

  // Handler unificado para pointer move (mouse/touch)
  const handlePointerMove = (e) => {
    // Processamento de Pan ativo
    if (isPanning && panStartRef.current) {
      const clientX = e.evt.clientX ?? e.evt.touches?.[0]?.clientX ?? e.evt.changedTouches?.[0]?.clientX;
      const clientY = e.evt.clientY ?? e.evt.touches?.[0]?.clientY ?? e.evt.changedTouches?.[0]?.clientY;
      if (clientX !== undefined && clientY !== undefined) {
        setStagePos({
          x: clientX - panStartRef.current.x,
          y: clientY - panStartRef.current.y
        });
      }
      return;
    }

    if (tool !== 'select') {
      handleStageMouseMove(e);
      return;
    }

    if (!draggingId || !dragStartRef.current) return;

    const stage = e.target.getStage();
    const pos = getPointerWorldPosition(stage);
    if (!pos) return;

    const { pos: startPos, element, isDragThresholdPassed } = dragStartRef.current;
    const dx = pos.x - startPos.x;
    const dy = pos.y - startPos.y;
    const distanceMoved = Math.hypot(dx, dy);

    // Limiar de arraste (Drag Threshold de 8 pixels):
    // Se o dedo moveu menos de 8px no tablet, considera apenas um TOQUE de seleção,
    // NÃO move o objeto e não o deixa 'preso' ou deslocado.
    if (!isDragThresholdPassed) {
      if (distanceMoved < 8) {
        return;
      }
      dragStartRef.current.isDragThresholdPassed = true;
    }

    setElements(prev =>
      prev.map(el => {
        if (el.id !== draggingId) return el;

        if (el.type === 'rectangle' || el.type === 'circle' || el.type === 'door_arc' || el.type === 'window' || el.type === 'technical_point' || el.type === 'text' || el.type === 'appliance') {
          return { ...el, x: Math.round(element.x + dx), y: Math.round(element.y + dy) };
        } else if (el.type === 'dimension' || el.type === 'line') {
          const [x1, y1, x2, y2] = element.points;
          return { ...el, points: [Math.round(x1 + dx), Math.round(y1 + dy), Math.round(x2 + dx), Math.round(y2 + dy)] };
        } else if (el.type === 'freehand') {
          const origPoints = element.points || [];
          return {
            ...el,
            points: origPoints.map((pt, pIdx) => (pIdx % 2 === 0 ? Math.round(pt + dx) : Math.round(pt + dy)))
          };
        }
        return el;
      })
    );
  };

  // Handler unificado para pointer up (mouse/touch)
  const handlePointerUp = (e) => {
    if (isPanning) {
      setIsPanning(false);
      panStartRef.current = null;
      return;
    }

    if (tool === 'select') {
      setDraggingId(null);
      dragStartRef.current = null;
    } else {
      handleStageMouseUp(e);
    }
  };

  // ========================================================
  // GESTOS TOUCH PARA TABLETS: PINCH ZOOM & 2-FINGER PAN
  // ========================================================
  const handleStageTouchStart = (e) => {
    e.evt.preventDefault();
    const touches = e.evt.touches;
    if (!touches) return;

    // Gesto de 2 dedos no tablet: ativa Pinch-to-Zoom e Pan suave com 2 dedos
    if (touches.length >= 2) {
      isTouchPinchingRef.current = true;
      isTouchPanningRef.current = false;
      touchPanStartRef.current = null;
      setIsDrawing(false);
      setDraggingId(null);

      const dist = Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY
      );
      const center = {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2
      };

      touchStartDistRef.current = dist;
      touchStartCenterRef.current = center;
      touchStartScaleRef.current = stageScale;
      touchStartStagePosRef.current = { ...stagePos };
      return;
    }

    // Gesto de 1 dedo no tablet
    if (touches.length === 1) {
      isTouchPinchingRef.current = false;
      touchStartDistRef.current = null;
      touchStartCenterRef.current = null;

      // Se a ferramenta for 'select' (Mover), verificar se o toque foi em elemento ou na área livre
      if (tool === 'select') {
        const stage = e.target.getStage();
        const pos = getPointerWorldPosition(stage);

        let hitElement = null;
        if (pos) {
          const { x, y } = pos;
          const allVisibleElements = activePage.layers
            .filter(layer => layer.visible)
            .flatMap(layer => layer.elements);

          for (let i = allVisibleElements.length - 1; i >= 0; i--) {
            const el = allVisibleElements[i];
            if (el.type === 'rectangle' && x >= el.x && x <= el.x + el.width && y >= el.y && y <= el.y + el.height) {
              hitElement = el; break;
            } else if ((el.type === 'line' || el.type === 'dimension') && distancePointToSegment(x, y, el.points[0], el.points[1], el.points[2], el.points[3]) <= 25) {
              hitElement = el; break;
            } else if (el.type === 'technical_point' && Math.hypot(x - el.x, y - el.y) <= 30) {
              hitElement = el; break;
            } else if (el.type === 'appliance') {
              const hw = (el.width || 60) / 2;
              const hh = (el.height || 60) / 2;
              if (x >= el.x - hw && x <= el.x + hw && y >= el.y - hh && y <= el.y + hh) {
                hitElement = el; break;
              }
            } else if (el.type === 'text') {
              if (x >= el.x && x <= el.x + (el.text?.length || 5) * 12 && y >= el.y - 16 && y <= el.y + 16) {
                hitElement = el; break;
              }
            }
          }
        }

        if (hitElement) {
          isTouchPanningRef.current = false;
          touchPanStartRef.current = null;
          handlePointerDown(e);
        } else {
          // Tocou na área vazia no modo Mover: Pan com 1 dedo no tablet!
          isTouchPanningRef.current = true;
          touchPanStartRef.current = {
            x: touches[0].clientX - stagePos.x,
            y: touches[0].clientY - stagePos.y
          };
          setSelectedId(null);
          setDraggingId(null);
        }
      } else {
        handlePointerDown(e);
      }
    }
  };

  const handleStageTouchMove = (e) => {
    e.evt.preventDefault();
    const touches = e.evt.touches;
    if (!touches) return;

    // Pinch Zoom e Pan com 2 dedos no tablet
    if (touches.length >= 2 && isTouchPinchingRef.current && touchStartDistRef.current) {
      const newDist = Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY
      );
      const newCenter = {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2
      };

      const initialDist = touchStartDistRef.current;
      const initialScale = touchStartScaleRef.current || stageScale;
      const initialCenter = touchStartCenterRef.current || newCenter;
      const initialStagePos = touchStartStagePosRef.current || stagePos;

      if (initialDist > 0) {
        const scaleChange = newDist / initialDist;
        let newScale = initialScale * scaleChange;
        newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));

        // Ponto virtual do canvas sob o centro dos dedos
        const virtualPoint = {
          x: (initialCenter.x - initialStagePos.x) / initialScale,
          y: (initialCenter.y - initialStagePos.y) / initialScale,
        };

        // Nova posição do canvas: focal zoom + deslocamento dos dedos
        const newPos = {
          x: newCenter.x - virtualPoint.x * newScale,
          y: newCenter.y - virtualPoint.y * newScale,
        };

        setStageScale(newScale);
        setStagePos(newPos);
      }
      return;
    }

    // Pan com 1 dedo no tablet (arraste na área livre em modo Mover)
    if (touches.length === 1 && isTouchPanningRef.current && touchPanStartRef.current) {
      setStagePos({
        x: touches[0].clientX - touchPanStartRef.current.x,
        y: touches[0].clientY - touchPanStartRef.current.y
      });
      return;
    }

    // Movimento com 1 dedo para desenho ou arraste de elemento
    handlePointerMove(e);
  };

  const handleStageTouchEnd = (e) => {
    e.evt.preventDefault();
    const touches = e.evt.touches;

    if (!touches || touches.length === 0) {
      isTouchPinchingRef.current = false;
      isTouchPanningRef.current = false;
      touchStartDistRef.current = null;
      touchStartCenterRef.current = null;
      touchStartScaleRef.current = null;
      touchStartStagePosRef.current = null;
      touchPanStartRef.current = null;
      handlePointerUp(e);
    } else if (touches.length === 1 && isTouchPinchingRef.current) {
      // Passou de 2 dedos para 1 dedo: reinicia referências de pinch
      isTouchPinchingRef.current = false;
      touchStartDistRef.current = null;
      touchStartCenterRef.current = null;
    }
  };

  const handleSave = async () => {
    try {
      // Obter posição/tamanho atual do Group da imagem de fundo
      const bgGroup = bgImageGroupRef.current;
      const currentPageBackgroundTransform = bgGroup ? {
        x: bgGroup.x(),
        y: bgGroup.y(),
        width: bgGroup.width(),
        height: bgGroup.height(),
        scaleX: bgGroup.scaleX(),
        scaleY: bgGroup.scaleY(),
        rotation: bgGroup.rotation()
      } : null;

      // Salvar canvas_data incluyendo estrutura de páginas e camadas
      // PC-3 FIX: backgroundImage e backgroundTransform agora estão dentro de cada página
      const pagesWithBackgroundTransform = pages.map(page =>
        page.id === activePageId
          ? { ...page, backgroundTransform: currentPageBackgroundTransform }
          : page
      );

      await sketchService.updateProject(projectId, {
        canvas_data: {
          pages: pagesWithBackgroundTransform,
          scale: 50,
          unit: 'cm'
        }
      });

      // Consolidar todos os elementos de todas as páginas/camadas para o backend
      // PC-4 FIX: Incluir pageId e layerId em cada elemento
      const allElements = [];
      pages.forEach(page => {
        page.layers.forEach(layer => {
          layer.elements.forEach(el => {
            allElements.push({
              type: el.type,
              data: {
                id: el.id,
                x: el.x,
                y: el.y,
                width: el.width,
                height: el.height,
                points: el.points,
                svgId: el.svgId,
                name: el.name,
                pointType: el.pointType,
                pointCategory: el.pointCategory,
                heightFromFloorCm: el.heightFromFloorCm,
                notes: el.notes,
                rotation: el.rotation,
                text: el.text,
                fontSize: el.fontSize,
                color: el.color,
                strokeWidth: el.strokeWidth
              },
              measurements: el.measurements,
              linkedProductId: el.linkedProductId || null,
              materialType: el.materialType || null,
              pageId: page.id,
              layerId: layer.id
            });
          });
        });
      });

      // Salvar elementos no backend
      await sketchService.saveElements(projectId, allElements);

      // Gerar e enviar snapshot de imagem do croqui
      if (stageRef.current) {
        const dataUrl = stageRef.current.toDataURL({
          mimeType: 'image/png',
          pixelRatio: 2,
          quality: 1
        });
        try {
          await sketchService.saveSnapshot(projectId, dataUrl);
        } catch (snapErr) {
          console.warn('Erro ao salvar imagem do croqui (continuando sem imagem):', snapErr);
        }
      }

      const isOfflineMode = !navigator.onLine || String(projectId).startsWith('local_');
      if (isOfflineMode) {
        toast.success('Croqui salvo offline no dispositivo! Será sincronizado automaticamente ao conectar à internet.');
      } else {
        toast.success('Croqui salvo com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar croqui: ' + (error.message || 'Falha desconhecida'));
    }
  };

  const selectedElement = elements.find(el => el.id === selectedId);

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Header - Modernizado e Responsivo */}
      <div className="bg-slate-900 text-white px-3 py-2 sm:px-5 sm:py-2.5 shadow-md z-30">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors flex items-center justify-center"
              title="Voltar para a lista de croquis"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 font-mono font-semibold">
                  CROQUI 2D
                </span>
                {!navigator.onLine && (
                  <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 font-mono font-bold flex items-center gap-1">
                    ⚡ OFFLINE
                  </span>
                )}
                <h1 className="text-sm sm:text-base font-bold text-white truncate max-w-[150px] sm:max-w-[320px]">
                  {project?.name || 'Croqui Técnico'}
                </h1>
              </div>
              {project?.display_client_name && (
                <p className="text-[11px] text-slate-400 truncate max-w-[200px] sm:max-w-[300px]">
                  Cliente: <span className="text-slate-200 font-medium">{project.display_client_name}</span>
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Botões Desfazer (Undo) e Refazer (Redo) */}
            <div className="flex items-center gap-0.5 bg-slate-800/80 border border-slate-700/80 rounded-lg p-0.5">
              <button
                type="button"
                onClick={handleUndo}
                disabled={historyStep <= 0}
                className="p-1.5 text-slate-300 hover:text-white hover:bg-slate-700/80 rounded transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                title="Desfazer última ação (Ctrl + Z)"
              >
                <Undo2 className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={handleRedo}
                disabled={historyStep >= history.length - 1}
                className="p-1.5 text-slate-300 hover:text-white hover:bg-slate-700/80 rounded transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                title="Refazer ação (Ctrl + Y)"
              >
                <Redo2 className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Seletor e Criador de Páginas */}
            <div className="flex items-center gap-1 bg-slate-800/80 border border-slate-700/80 rounded-lg p-0.5">
              <select
                value={activePageId}
                onChange={(e) => {
                  const page = pages.find(p => p.id === e.target.value);
                  setActivePageId(e.target.value);
                  setActiveLayerId(page?.layers[0]?.id);
                }}
                className="text-xs bg-transparent text-slate-200 px-2 py-1 focus:outline-none cursor-pointer"
              >
                {pages.map((page, idx) => (
                  <option key={page.id} value={page.id} className="bg-slate-900 text-white">
                    {page.name || `Página ${idx + 1}`}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={addPage}
                className="p-1 text-slate-300 hover:text-white hover:bg-slate-700/80 rounded transition-colors"
                title="Criar Nova Página"
              >
                <Plus className="w-3.5 h-3.5 text-emerald-400" />
              </button>
              {pages.length > 1 && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Deseja excluir a ${activePage.name || 'página atual'}?`)) {
                      deletePage(activePageId);
                    }
                  }}
                  className="p-1 text-slate-400 hover:text-red-400 hover:bg-slate-700/80 rounded transition-colors"
                  title="Excluir Página Atual"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Botão Mobile de Camadas */}
            <button
              type="button"
              onClick={() => setShowMobileLayersModal(true)}
              className="md:hidden flex items-center gap-1 bg-slate-800 text-slate-200 border border-slate-700 px-2.5 py-1.5 rounded-lg text-xs font-semibold"
              title="Gerenciar Camadas"
            >
              <Layers className="w-3.5 h-3.5 text-blue-400" />
              <span>Camadas ({activePage.layers.length})</span>
            </button>

            {/* Toggle Painel Lateral Desktop/Tablet */}
            <button
              type="button"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${
                isSidebarOpen 
                  ? 'bg-slate-800 border-slate-700 text-blue-400' 
                  : 'bg-slate-800/60 border-slate-700/60 text-slate-400 hover:text-white'
              }`}
              title="Alternar Painel Lateral"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              <span>{isSidebarOpen ? 'Ocultar Painel' : 'Painel'}</span>
            </button>

            {/* Botão Mobile/Tablet de Propriedades do Elemento */}
            {selectedId && (
              <button
                type="button"
                onClick={() => setShowMobileProperties(!showMobileProperties)}
                className="md:hidden flex items-center gap-1 bg-amber-500 text-slate-950 font-bold px-2.5 py-1.5 rounded-lg text-xs shadow-sm"
              >
                <span>⚙️ Ajustar</span>
              </button>
            )}

            <button
              onClick={handleSave}
              className="flex items-center gap-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm shadow-sm transition-all"
            >
              <Save className="w-4 h-4" />
              <span>Salvar</span>
            </button>
          </div>
        </div>

        {/* Barra de Status e Ferramenta Ativa */}
        <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-slate-800/80 text-[11px] text-slate-400">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Ferramenta:</span>
            <span className={`px-2 py-0.5 rounded-full font-medium ${
              tool === 'select' ? 'bg-emerald-500/20 text-emerald-400' :
              tool === 'rectangle' ? 'bg-blue-500/20 text-blue-400' :
              tool === 'circle' ? 'bg-blue-500/20 text-blue-400' :
              tool === 'door_arc' ? 'bg-indigo-500/20 text-indigo-300 font-bold' :
              tool === 'window' ? 'bg-sky-500/20 text-sky-400 font-bold' :
              tool === 'line' ? 'bg-purple-500/20 text-purple-400' :
              tool === 'dimension' ? 'bg-indigo-500/20 text-indigo-300 font-bold' :
              tool === 'technical_point' ? 'bg-amber-500/20 text-amber-300 font-bold' :
              tool === 'pen' ? 'bg-orange-500/20 text-orange-400' :
              'bg-pink-500/20 text-pink-400'
            }`}>
              {tool === 'select' && '👆 Seleção / Mover'}
              {tool === 'rectangle' && '▢ Módulo Retangular'}
              {tool === 'circle' && '◯ Círculo / Pilar'}
              {tool === 'door_arc' && '🚪 Arco de Porta'}
              {tool === 'window' && '🪟 Janela Técnica'}
              {tool === 'line' && '─ Linha'}
              {tool === 'dimension' && '📏 Cota de Medição'}
              {tool === 'technical_point' && '⚡ Ponto de Instalação'}
              {tool === 'pen' && `✏️ Caneta ${penMode === 'freehand' ? '(Livre)' : '(Reta)'}`}
              {tool === 'text' && 'T Texto'}
            </span>
            {selectedId && (
              <span className="bg-red-500/20 text-red-300 border border-red-500/30 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                <Check className="w-3 h-3" /> Elemento Selecionado
              </span>
            )}
          </div>

          <div className="hidden sm:flex items-center gap-3 text-slate-400 font-medium">
            <span>Escala: <strong className="text-slate-200">1:{project?.scale || 50}</strong></span>
            <span>Unidade: <strong className="text-slate-200">{project?.unit || 'cm'}</strong></span>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden relative pb-20 md:pb-0">
        {/* Toolbar Desktop / Tablet Lateral */}
        <div className="hidden md:flex w-20 bg-slate-900 border-r border-slate-800 flex-col items-center py-2 sm:py-3 space-y-2 z-20 shadow-lg overflow-y-auto overflow-x-hidden no-scrollbar select-none shrink-0">
          <div className="flex flex-col items-center gap-1 w-full px-1.5">
            <button
              onClick={() => setTool('select')}
              className={`p-2 w-full flex flex-col items-center gap-0.5 rounded-xl transition-all ${
                tool === 'select'
                  ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              title="Mover e Selecionar"
            >
              <MousePointer className="w-5 h-5" />
              <span className="text-[10px] font-semibold tracking-tight">Mover</span>
            </button>

            <button
              onClick={() => setTool('rectangle')}
              className={`p-2 w-full flex flex-col items-center gap-0.5 rounded-xl transition-all ${
                tool === 'rectangle'
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              title="Desenhar Módulo / Parede Retangular"
            >
              <Square className="w-5 h-5" />
              <span className="text-[10px] font-semibold tracking-tight">Módulo</span>
            </button>

            <button
              onClick={() => setTool('circle')}
              className={`p-2 w-full flex flex-col items-center gap-0.5 rounded-xl transition-all ${
                tool === 'circle'
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              title="Círculo / Pilar / Mesa Redonda"
            >
              <CircleIcon className="w-5 h-5" />
              <span className="text-[10px] font-semibold tracking-tight">Círculo</span>
            </button>

            <button
              onClick={() => setTool('door_arc')}
              className={`p-2 w-full flex flex-col items-center gap-0.5 rounded-xl transition-all ${
                tool === 'door_arc'
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/30 font-bold'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              title="Arco de Abertura de Porta (90°)"
            >
              <DoorOpen className="w-5 h-5" />
              <span className="text-[10px] font-semibold tracking-tight">Porta</span>
            </button>

            <button
              onClick={() => setTool('window')}
              className={`p-2 w-full flex flex-col items-center gap-0.5 rounded-xl transition-all ${
                tool === 'window'
                  ? 'bg-sky-600 text-white shadow-md shadow-sky-500/30 font-bold'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              title="Janela Técnica 2D"
            >
              <AppWindow className="w-5 h-5" />
              <span className="text-[10px] font-semibold tracking-tight">Janela</span>
            </button>

            <button
              onClick={() => setTool('dimension')}
              className={`p-2 w-full flex flex-col items-center gap-0.5 rounded-xl transition-all ${
                tool === 'dimension'
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/30 font-bold'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              title="Cota com Ticks Arquitetônicos"
            >
              <Compass className="w-5 h-5" />
              <span className="text-[10px] font-semibold tracking-tight">Cota</span>
            </button>

            <button
              onClick={() => setTool('technical_point')}
              className={`p-2 w-full flex flex-col items-center gap-0.5 rounded-xl transition-all ${
                tool === 'technical_point'
                  ? 'bg-amber-500 text-slate-950 font-bold shadow-md shadow-amber-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              title="Pontos de Instalação (Tomadas, Água, Gás)"
            >
              <Zap className="w-5 h-5" />
              <span className="text-[10px] font-semibold tracking-tight">Pontos</span>
            </button>

            <button
              onClick={() => setTool('line')}
              className={`p-2 w-full flex flex-col items-center gap-0.5 rounded-xl transition-all ${
                tool === 'line'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              title="Linha Simples"
            >
              <Ruler className="w-5 h-5" />
              <span className="text-[10px] font-semibold tracking-tight">Linha</span>
            </button>

            <button
              onClick={() => {
                if (tool === 'pen') {
                  // Se já estiver na caneta, alterna modo livre <-> reta
                  setPenMode(prev => prev === 'freehand' ? 'line' : 'freehand');
                } else {
                  setTool('pen');
                }
              }}
              className={`p-2 w-full flex flex-col items-center gap-0.5 rounded-xl transition-all ${
                tool === 'pen'
                  ? 'bg-orange-500 text-white shadow-md shadow-orange-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              title={`Caneta Manual - Modo: ${penMode === 'freehand' ? 'Livre' : 'Reta'} (Clique para alternar)`}
            >
              <PenTool className="w-5 h-5" />
              <span className="text-[10px] font-semibold tracking-tight">
                {tool === 'pen' ? (penMode === 'freehand' ? 'Livre' : 'Reta') : 'Caneta'}
              </span>
            </button>

            <button
              onClick={() => setTool('eraser')}
              className={`p-2 w-full flex flex-col items-center gap-0.5 rounded-xl transition-all ${
                tool === 'eraser'
                  ? 'bg-rose-600 text-white shadow-md shadow-rose-600/30 font-bold'
                  : 'text-slate-400 hover:text-rose-400 hover:bg-slate-800'
              }`}
              title="Borracha (Clique sobre qualquer elemento para apagar)"
            >
              <Eraser className="w-5 h-5" />
              <span className="text-[10px] font-semibold tracking-tight">Apagar</span>
            </button>

            <button
              onClick={() => setTool('text')}
              className={`p-2 w-full flex flex-col items-center gap-0.5 rounded-xl transition-all ${
                tool === 'text'
                  ? 'bg-pink-600 text-white shadow-md shadow-pink-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              title="Texto Técnico"
            >
              <Type className="w-5 h-5" />
              <span className="text-[10px] font-semibold tracking-tight">Texto</span>
            </button>

            <button
              onClick={() => setShowCatalogDrawer(!showCatalogDrawer)}
              className={`p-2 w-full flex flex-col items-center gap-0.5 rounded-xl transition-all ${
                showCatalogDrawer
                  ? 'bg-teal-500 text-slate-950 font-bold shadow-md shadow-teal-500/30 ring-2 ring-teal-400'
                  : 'text-slate-400 hover:text-teal-400 hover:bg-slate-800'
              }`}
              title="Catálogo de Eletros e Objetos 2D"
            >
              <Package className="w-5 h-5" />
              <span className="text-[10px] font-semibold tracking-tight">Eletros</span>
            </button>
          </div>

          <div className="border-t border-slate-800 w-12 my-0.5" />

          {/* Grade e Imagem */}
          <div className="flex flex-col items-center gap-1 w-full px-1.5">
            <button
              onClick={() => setShowGrid(!showGrid)}
              className={`p-2 w-full flex flex-col items-center gap-0.5 rounded-xl transition-all ${
                showGrid
                  ? 'bg-slate-800 text-blue-400 border border-slate-700'
                  : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/60'
              }`}
              title="Alternar Grade"
            >
              <Grid className="w-4 h-4" />
              <span className="text-[9px] font-medium">Grade</span>
            </button>

            <div className="relative w-full">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
              />
              <label
                htmlFor="image-upload"
                className={`p-2 w-full flex flex-col items-center gap-0.5 rounded-xl cursor-pointer transition-all ${
                  backgroundImage
                    ? 'bg-purple-900/60 text-purple-300 border border-purple-500/50'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                title="Inserir Foto do Ambiente (Calco)"
              >
                <Image className="w-4 h-4" />
                <span className="text-[9px] font-medium">Calco</span>
              </label>
            </div>

            {backgroundImage && (
              <button
                onClick={handleRemoveBackgroundImage}
                className="p-1.5 w-full flex items-center justify-center rounded-xl text-red-400 hover:bg-red-500/20 transition-colors"
                title="Remover Foto de Fundo"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="pt-1 border-t border-slate-800 w-full px-1.5 flex flex-col items-center gap-1.5 pb-2">
            <button
              onClick={() => {
                if (selectedId) {
                  deleteElement(selectedId);
                  setSelectedId(null);
                }
              }}
              disabled={!selectedId}
              className="p-2 w-full flex flex-col items-center gap-0.5 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 disabled:opacity-20 transition-all border border-red-500/20"
              title="Deletar Elemento Selecionado"
            >
              <Trash2 className="w-4 h-4" />
              <span className="text-[9px] font-semibold">Excluir</span>
            </button>
          </div>
        </div>

        {/* Drawer Lateral do Catálogo de Eletros e Objetos */}
        <ApplianceCatalogDrawer
          isOpen={showCatalogDrawer}
          onClose={() => setShowCatalogDrawer(false)}
          onSelectItem={insertApplianceItem}
        />

        {/* Canvas */}
        <div 
          ref={canvasContainerRef} 
          onDragOver={handleCanvasDragOver}
          onDrop={handleCanvasDrop}
          className="flex-1 bg-gray-100 overflow-hidden relative flex items-center justify-center" 
          style={{ touchAction: 'none' }}
        >
          {/* Barra Flutuante de Calco (Papel Manteiga) */}
          <div className="absolute top-2 left-2 z-20">
            <CalcoControls
              hasImage={Boolean(bgImageElement?.src)}
              opacity={calcoOpacity}
              setOpacity={setCalcoOpacity}
              isLocked={calcoLocked}
              setIsLocked={setCalcoLocked}
              isVisible={calcoVisible}
              setIsVisible={setCalcoVisible}
              onUploadClick={() => fileInputRef.current?.click()}
            />
          </div>

          {/* Dica de Navegação Pan/Zoom Flutuante (Desktop) */}
          <div className="hidden lg:flex absolute top-2 right-2 z-20 items-center gap-2 bg-slate-900/80 text-slate-300 px-3 py-1.5 rounded-xl border border-slate-700/80 text-[11px] backdrop-blur-sm pointer-events-none select-none">
            <span>Roda do Mouse: <strong>Zoom</strong></span>
            <span>•</span>
            <span>Espaço + Arraste ou Botão Meio: <strong>Pan</strong></span>
          </div>

          {/* Barra de Controles Rápidos da Caneta (Flutuante quando ferramenta Pen está ativa) */}
          {tool === 'pen' && (
            <div className="absolute top-14 sm:top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 sm:gap-2 bg-slate-900/95 text-white px-3 py-1.5 rounded-2xl shadow-2xl backdrop-blur-md border border-slate-700/90 select-none animate-in fade-in duration-150">
              <span className="text-[11px] font-bold text-orange-400 hidden sm:inline mr-0.5">✏️ Caneta:</span>

              {/* Seletor Modo: Livre vs Reto */}
              <div className="flex items-center bg-slate-800/90 p-0.5 rounded-xl border border-slate-700/80">
                <button
                  type="button"
                  onClick={() => setPenMode('freehand')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                    penMode === 'freehand'
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'text-slate-400 hover:text-white'
                  }`}
                  title="Traçado Livre Orgânico"
                >
                  Livre
                </button>
                <button
                  type="button"
                  onClick={() => setPenMode('line')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                    penMode === 'line'
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'text-slate-400 hover:text-white'
                  }`}
                  title="Traçado Reto com a maleabilidade da caneta"
                >
                  Reta
                </button>
              </div>

              <div className="w-px h-4 bg-slate-700 mx-0.5" />

              {/* Controles Rápidos de Espessura (1px, 2px, 4px, 6px, 8px) */}
              <div className="flex items-center gap-1">
                {[1, 2, 4, 6, 8].map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => setBrushSize(size)}
                    className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
                      brushSize === size
                        ? 'bg-orange-500/20 text-orange-400 border border-orange-500/60 font-bold'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                    }`}
                    title={`Espessura ${size}px`}
                  >
                    <div
                      className="rounded-full bg-current"
                      style={{
                        width: `${Math.max(3, Math.min(14, size * 2))}px`,
                        height: `${Math.max(3, Math.min(14, size * 2))}px`
                      }}
                    />
                  </button>
                ))}
              </div>

              <div className="w-px h-4 bg-slate-700 mx-0.5" />

              {/* Cores rápidas da caneta */}
              <div className="flex items-center gap-1">
                {['#000000', '#ef4444', '#2563eb', '#16a34a', '#d97706'].map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setBrushColor(color)}
                    className={`w-5 h-5 rounded-full border-2 transition-transform ${
                      brushColor === color ? 'scale-115 border-white shadow-sm' : 'border-slate-600 opacity-70 hover:opacity-100'
                    }`}
                    style={{ backgroundColor: color }}
                    title={`Cor: ${color}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Canvas Stage com suporte nativo a Zoom e Pan via Konva */}
          <Stage
            ref={stageRef}
            width={stageSize.width}
            height={stageSize.height}
            scaleX={stageScale}
            scaleY={stageScale}
            x={stagePos.x}
            y={stagePos.y}
            listening={true}
            onWheel={handleWheel}
            onMouseDown={(e) => handlePointerDown(e)}
            onMouseMove={(e) => handlePointerMove(e)}
            onMouseUp={(e) => handlePointerUp(e)}
            onTouchStart={handleStageTouchStart}
            onTouchMove={handleStageTouchMove}
            onTouchEnd={handleStageTouchEnd}
            style={{
              cursor: isPanning
                ? 'grabbing'
                : isSpacePressed
                ? 'grab'
                : tool === 'select'
                ? 'default'
                : 'crosshair'
            }}
          >
            <Layer>
              {/* Grade expandida para cobrir toda a área de navegação */}
              {showGrid && (() => {
                const GRID_SIZE = 40;
                // Calcular limites visíveis do mundo virtual para estender o grid dinamicamente
                const startX = Math.floor((-stagePos.x / stageScale) / GRID_SIZE) * GRID_SIZE - GRID_SIZE * 5;
                const endX = Math.ceil(((stageSize.width - stagePos.x) / stageScale) / GRID_SIZE) * GRID_SIZE + GRID_SIZE * 5;
                const startY = Math.floor((-stagePos.y / stageScale) / GRID_SIZE) * GRID_SIZE - GRID_SIZE * 5;
                const endY = Math.ceil(((stageSize.height - stagePos.y) / stageScale) / GRID_SIZE) * GRID_SIZE + GRID_SIZE * 5;

                const vLines = [];
                for (let x = startX; x <= endX; x += GRID_SIZE) {
                  vLines.push(
                    <Line
                      key={`vg-${x}`}
                      points={[x, startY, x, endY]}
                      stroke="#e2e8f0"
                      strokeWidth={1 / stageScale}
                      listening={false}
                    />
                  );
                }

                const hLines = [];
                for (let y = startY; y <= endY; y += GRID_SIZE) {
                  hLines.push(
                    <Line
                      key={`hg-${y}`}
                      points={[startX, y, endX, y]}
                      stroke="#e2e8f0"
                      strokeWidth={1 / stageScale}
                      listening={false}
                    />
                  );
                }

                return (
                  <Group listening={false}>
                    {vLines}
                    {hLines}
                  </Group>
                );
              })()}
              
              {/* Elementos salvos - cada camada visível em Group separado (Layer não pode conter Layer) */}
              {activePage.layers
                .filter(layer => layer.visible)
                .map(layer => (
                  <Group key={layer.id}>
                    {layer.elements.map(element => {
                      const isSelected = selectedId === element.id;

                      if (element.type === 'rectangle') {
                        return (
                          <Group key={element.id}>
                            <Rect
                              x={element.x}
                              y={element.y}
                              width={element.width}
                              height={element.height}
                              stroke={isSelected ? '#ef4444' : '#2563eb'}
                              strokeWidth={isSelected ? 3 : 2}
                              fill="transparent"
                              draggable={tool === 'select'}
                              listening={tool === 'select' || tool === 'eraser'}
                              onClick={() => handleElementClick(element.id)}
                              onTap={() => handleElementClick(element.id)}
                              onDragEnd={(e) => {
                                updateElement(element.id, {
                                  x: Math.round(e.target.x()),
                                  y: Math.round(e.target.y())
                                });
                              }}
                            />
                            {isSelected && (
                              <Circle
                                x={element.x + element.width}
                                y={element.y + element.height}
                                radius={8}
                                fill="#ffffff"
                                stroke="#2563eb"
                                strokeWidth={2.5}
                                hitStrokeWidth={25}
                                draggable
                                onDragStart={(e) => { e.cancelBubble = true; }}
                                onDragMove={(e) => {
                                  e.cancelBubble = true;
                                  handleRectCornerDrag(element.id, e);
                                }}
                                onDragEnd={(e) => {
                                  e.cancelBubble = true;
                                }}
                              />
                            )}
                          </Group>
                        );
                      } else if (element.type === 'circle') {
                        const r = element.radius || 30;
                        return (
                          <Group key={element.id}>
                            <Circle
                              x={element.x}
                              y={element.y}
                              radius={r}
                              stroke={isSelected ? '#ef4444' : '#2563eb'}
                              strokeWidth={isSelected ? 3 : 2}
                              fill={isSelected ? 'rgba(239, 68, 68, 0.05)' : 'transparent'}
                              draggable={tool === 'select'}
                              listening={tool === 'select' || tool === 'eraser'}
                              onClick={() => handleElementClick(element.id)}
                              onTap={() => handleElementClick(element.id)}
                              onDragEnd={(e) => {
                                updateElement(element.id, {
                                  x: Math.round(e.target.x()),
                                  y: Math.round(e.target.y())
                                });
                              }}
                            />
                            {isSelected && (
                              <Group>
                                {/* Alça Dinâmica de Redimensionamento do Círculo na Borda Direita */}
                                <Circle
                                  x={element.x + r}
                                  y={element.y}
                                  radius={8}
                                  fill="#ffffff"
                                  stroke="#2563eb"
                                  strokeWidth={2.5}
                                  hitStrokeWidth={25}
                                  draggable
                                  onDragStart={(e) => { e.cancelBubble = true; }}
                                  onDragMove={(e) => {
                                    e.cancelBubble = true;
                                    handleCircleRadiusDrag(element.id, e);
                                  }}
                                  onDragEnd={(e) => {
                                    e.cancelBubble = true;
                                  }}
                                />
                                {/* Alça Dinâmica Superior */}
                                <Circle
                                  x={element.x}
                                  y={element.y - r}
                                  radius={8}
                                  fill="#ffffff"
                                  stroke="#2563eb"
                                  strokeWidth={2.5}
                                  hitStrokeWidth={25}
                                  draggable
                                  onDragStart={(e) => { e.cancelBubble = true; }}
                                  onDragMove={(e) => {
                                    e.cancelBubble = true;
                                    handleCircleRadiusDrag(element.id, e);
                                  }}
                                  onDragEnd={(e) => {
                                    e.cancelBubble = true;
                                  }}
                                />
                              </Group>
                            )}
                          </Group>
                        );
                      } else if (element.type === 'door_arc') {
                        const w = element.width || 80;
                        const rot = element.rotation || 0;
                        const isFlipped = element.flipped || false;
                        return (
                          <Group key={element.id}>
                            <Group
                              x={element.x}
                              y={element.y}
                              rotation={rot}
                              scaleX={isFlipped ? -1 : 1}
                              draggable={tool === 'select'}
                              listening={tool === 'select' || tool === 'eraser'}
                              onClick={() => handleElementClick(element.id)}
                              onTap={() => handleElementClick(element.id)}
                              onDragEnd={(e) => {
                                updateElement(element.id, {
                                  x: Math.round(e.target.x()),
                                  y: Math.round(e.target.y())
                                });
                              }}
                            >
                              {/* Batente da porta (folha aberta 90 graus) */}
                              <Line
                                points={[0, 0, 0, -w]}
                                stroke={isSelected ? '#ef4444' : '#2563eb'}
                                strokeWidth={isSelected ? 3.5 : 2.5}
                              />
                              {/* Vão livre horizontal */}
                              <Line
                                points={[0, 0, w, 0]}
                                stroke={isSelected ? '#ef4444' : '#94a3b8'}
                                strokeWidth={1.5}
                                dash={[3, 3]}
                              />
                              {/* Arco de varredura da porta */}
                              <Line
                                points={(() => {
                                  const pts = [];
                                  for (let a = 0; a <= 90; a += 10) {
                                    const rad = (a * Math.PI) / 180;
                                    pts.push(Math.sin(rad) * w, -Math.cos(rad) * w);
                                  }
                                  return pts;
                                })()}
                                stroke={isSelected ? '#ef4444' : '#3b82f6'}
                                strokeWidth={1.5}
                                dash={[4, 4]}
                              />
                            </Group>
                            {/* Alça Dinâmica de Redimensionamento do Vão da Porta */}
                            {isSelected && (
                              <Circle
                                x={element.x + Math.cos((rot * Math.PI) / 180) * (isFlipped ? -w : w)}
                                y={element.y + Math.sin((rot * Math.PI) / 180) * w}
                                radius={8}
                                fill="#ffffff"
                                stroke="#2563eb"
                                strokeWidth={2.5}
                                hitStrokeWidth={25}
                                draggable
                                onDragStart={(e) => { e.cancelBubble = true; }}
                                onDragMove={(e) => {
                                  e.cancelBubble = true;
                                  handleDoorWidthDrag(element.id, e);
                                }}
                                onDragEnd={(e) => {
                                  e.cancelBubble = true;
                                }}
                              />
                            )}
                          </Group>
                        );
                      } else if (element.type === 'window') {
                        const w = element.width || 100;
                        const h = element.height || 15;
                        const rot = element.rotation || 0;
                        return (
                          <Group
                            key={element.id}
                            x={element.x}
                            y={element.y}
                            rotation={rot}
                            draggable={tool === 'select'}
                            listening={tool === 'select' || tool === 'eraser'}
                            onClick={() => handleElementClick(element.id)}
                            onTap={() => handleElementClick(element.id)}
                            onDragEnd={(e) => {
                              updateElement(element.id, {
                                x: Math.round(e.target.x()),
                                y: Math.round(e.target.y())
                              });
                            }}
                          >
                            {/* Vão da alvenaria (retângulo delimitador) */}
                            <Rect
                              x={0}
                              y={0}
                              width={w}
                              height={h}
                              stroke={isSelected ? '#ef4444' : '#0284c7'}
                              strokeWidth={isSelected ? 2.5 : 1.5}
                              fill="rgba(56, 189, 248, 0.12)"
                            />
                            {/* Linhas de vidro duplo central */}
                            <Line
                              points={[0, h * 0.35, w, h * 0.35]}
                              stroke={isSelected ? '#ef4444' : '#0369a1'}
                              strokeWidth={1.5}
                            />
                            <Line
                              points={[0, h * 0.65, w, h * 0.65]}
                              stroke={isSelected ? '#ef4444' : '#0369a1'}
                              strokeWidth={1.5}
                            />
                            {/* Batentes laterais */}
                            <Line
                              points={[0, 0, 0, h]}
                              stroke={isSelected ? '#ef4444' : '#0f172a'}
                              strokeWidth={2}
                            />
                            <Line
                              points={[w, 0, w, h]}
                              stroke={isSelected ? '#ef4444' : '#0f172a'}
                              strokeWidth={2}
                            />
                            {/* Rótulo de dimensões */}
                            <Text
                              text={`Janela ${Math.round(w)}cm (P:${element.peitorilCm || 100})`}
                              x={w / 2 - 35}
                              y={h + 4}
                              fontSize={10}
                              fontStyle="bold"
                              fill={isSelected ? '#ef4444' : '#0284c7'}
                              listening={false}
                            />
                          </Group>
                        );
                      } else if (element.type === 'line') {
                        const [x1, y1, x2, y2] = element.points;

                        return (
                          <Group key={element.id}>
                            <Line
                              points={element.points}
                              stroke={isSelected ? '#ef4444' : (element.color || '#2563eb')}
                              strokeWidth={isSelected ? 6 : (element.strokeWidth || 4)}
                              hitStrokeWidth={tool === 'select' ? 30 : 0}
                              listening={tool === 'select' || tool === 'eraser'}
                              onClick={() => handleElementClick(element.id)}
                              onTap={() => handleElementClick(element.id)}
                            />
                            {isSelected && (
                              <Group>
                                <Circle
                                  x={x1}
                                  y={y1}
                                  radius={6}
                                  fill="#ffffff"
                                  stroke="#2563eb"
                                  strokeWidth={2}
                                  draggable
                                  onDragMove={(e) => handleLinePointDrag(element.id, 0, e)}
                                />
                                <Circle
                                  x={x2}
                                  y={y2}
                                  radius={6}
                                  fill="#ffffff"
                                  stroke="#2563eb"
                                  strokeWidth={2}
                                  draggable
                                  onDragMove={(e) => handleLinePointDrag(element.id, 1, e)}
                                />
                              </Group>
                            )}
                            {element.measurements && (
                              <Text
                                text={`${Math.round(element.measurements.length)}cm`}
                                x={(x1 + x2) / 2 - 25}
                                y={(y1 + y2) / 2 - 25}
                                fontSize={12}
                                fill={isSelected ? '#ef4444' : '#6b7280'}
                                listening={false}
                              />
                            )}
                          </Group>
                        );
                      } else if (element.type === 'dimension') {
                        const [x1, y1, x2, y2] = element.points;
                        return (
                          <Group key={element.id}>
                            <DimensionLine
                              points={element.points}
                              measurements={element.measurements}
                              isSelected={isSelected}
                              scaleRatio={1}
                              unit={project?.unit || 'cm'}
                              listening={tool === 'select' || tool === 'eraser'}
                              onClick={() => handleElementClick(element.id)}
                              onTap={() => handleElementClick(element.id)}
                            />
                            {isSelected && (
                              <Group>
                                <Circle
                                  x={x1}
                                  y={y1}
                                  radius={7}
                                  fill="#ffffff"
                                  stroke="#ef4444"
                                  strokeWidth={2.5}
                                  draggable
                                  onDragMove={(e) => handleLinePointDrag(element.id, 0, e)}
                                />
                                <Circle
                                  x={x2}
                                  y={y2}
                                  radius={7}
                                  fill="#ffffff"
                                  stroke="#ef4444"
                                  strokeWidth={2.5}
                                  draggable
                                  onDragMove={(e) => handleLinePointDrag(element.id, 1, e)}
                                />
                              </Group>
                            )}
                          </Group>
                        );
                      } else if (element.type === 'technical_point') {
                        return (
                          <TechnicalPoint
                            key={element.id}
                            element={element}
                            isSelected={isSelected}
                            listening={tool === 'select' || tool === 'eraser'}
                            onClick={() => handleElementClick(element.id)}
                            onTap={() => handleElementClick(element.id)}
                          />
                        );
                      } else if (element.type === 'freehand') {
                        return (
                          <Line
                            key={element.id}
                            points={element.points}
                            stroke={element.color || brushColor}
                            strokeWidth={element.strokeWidth || brushSize}
                            tension={0.5}
                            lineCap="round"
                            lineJoin="round"
                            listening={tool === 'select' || tool === 'eraser'}
                            onClick={() => handleElementClick(element.id)}
                            onTap={() => handleElementClick(element.id)}
                          />
                        );
                      } else if (element.type === 'text') {
                        return (
                          <Text
                            key={element.id}
                            x={element.x}
                            y={element.y}
                            text={element.text}
                            fontSize={element.fontSize || 16}
                            fill={element.color || brushColor}
                            listening={tool === 'select' || tool === 'eraser'}
                            onClick={() => handleElementClick(element.id)}
                            onTap={() => handleElementClick(element.id)}
                          />
                        );
                      } else if (element.type === 'appliance') {
                        return (
                          <ApplianceElement
                            key={element.id}
                            element={element}
                            isSelected={isSelected}
                            draggable={tool === 'select'}
                            listening={tool === 'select' || tool === 'eraser'}
                            onClick={() => handleElementClick(element.id)}
                            onTap={() => handleElementClick(element.id)}
                            onDragEnd={(e) => {
                              updateElement(element.id, {
                                x: Math.round(e.target.x()),
                                y: Math.round(e.target.y())
                              });
                            }}
                          />
                        );
                      }

                      return null;
                    }).filter(Boolean)}
                  </Group>
                ))}
              
              {/* Desenho temporário */}
              {isDrawing && startPos && startPos.currentX && tool === 'rectangle' && (
                <Rect
                  x={Math.min(startPos.x, startPos.currentX)}
                  y={Math.min(startPos.y, startPos.currentY)}
                  width={Math.abs(startPos.currentX - startPos.x)}
                  height={Math.abs(startPos.currentY - startPos.y)}
                  stroke="#2563eb"
                  strokeWidth={2}
                  dash={[5, 5]}
                  fill="rgba(37, 99, 235, 0.1)"
                  listening={false}
                />
              )}

              {isDrawing && startPos && startPos.currentX && tool === 'circle' && (
                <Circle
                  x={startPos.x}
                  y={startPos.y}
                  radius={Math.hypot(startPos.currentX - startPos.x, startPos.currentY - startPos.y)}
                  stroke="#2563eb"
                  strokeWidth={2}
                  dash={[5, 5]}
                  fill="rgba(37, 99, 235, 0.05)"
                  listening={false}
                />
              )}

              {isDrawing && startPos && startPos.currentX && tool === 'door_arc' && (() => {
                const w = Math.hypot(startPos.currentX - startPos.x, startPos.currentY - startPos.y);
                const pts = [];
                for (let a = 0; a <= 90; a += 10) {
                  const rad = (a * Math.PI) / 180;
                  pts.push(startPos.x + Math.sin(rad) * w, startPos.y - Math.cos(rad) * w);
                }
                return (
                  <Group listening={false}>
                    <Line points={[startPos.x, startPos.y, startPos.x, startPos.y - w]} stroke="#2563eb" strokeWidth={2} />
                    <Line points={[startPos.x, startPos.y, startPos.x + w, startPos.y]} stroke="#94a3b8" strokeWidth={1.5} dash={[3, 3]} />
                    <Line points={pts} stroke="#3b82f6" strokeWidth={1.5} dash={[4, 4]} />
                  </Group>
                );
              })()}

              {isDrawing && startPos && startPos.currentX && tool === 'window' && (() => {
                const dist = Math.hypot(startPos.currentX - startPos.x, startPos.currentY - startPos.y);
                const w = dist > 15 ? dist : 100;
                const angleRad = Math.atan2(startPos.currentY - startPos.y, startPos.currentX - startPos.x);
                const angleDeg = (Math.round((angleRad * 180) / Math.PI / 15) * 15 + 360) % 360;
                return (
                  <Group x={startPos.x} y={startPos.y} rotation={dist > 15 ? angleDeg : 0} listening={false}>
                    <Rect x={0} y={0} width={w} height={15} stroke="#0284c7" strokeWidth={2} dash={[4, 4]} fill="rgba(56, 189, 248, 0.2)" />
                    <Line points={[0, 5, w, 5]} stroke="#0369a1" strokeWidth={1.5} />
                    <Line points={[0, 10, w, 10]} stroke="#0369a1" strokeWidth={1.5} />
                  </Group>
                );
              })()}
              
              {isDrawing && startPos && startPos.currentX && tool === 'line' && (
                <Line
                  points={[startPos.x, startPos.y, startPos.currentX, startPos.currentY]}
                  stroke="#2563eb"
                  strokeWidth={2}
                  dash={[5, 5]}
                  listening={false}
                />
              )}

              {/* Preview da caneta (freehand) */}
              {tool === 'pen' && isDrawing && penMode === 'freehand' && freehandPoints.length > 0 && (
                <Line
                  points={freehandPoints}
                  stroke={brushColor}
                  strokeWidth={brushSize}
                  tension={0.5}
                  lineCap="round"
                  lineJoin="round"
                  listening={false}
                />
              )}

              {/* Preview da caneta (linha reta) */}
              {tool === 'pen' && isDrawing && penMode === 'line' && startPos && startPos.currentX !== undefined && (
                <Line
                  points={[startPos.x, startPos.y, startPos.currentX, startPos.currentY]}
                  stroke={brushColor}
                  strokeWidth={brushSize}
                  lineCap="round"
                  listening={false}
                />
              )}
            </Layer>
          </Stage>

          {/* Controles Flutuantes de Zoom e Centralização (Desktop e Mobile) */}
          <div className="absolute bottom-16 md:bottom-4 right-4 z-30 flex items-center gap-1.5 bg-slate-900/90 text-white p-1.5 rounded-2xl shadow-2xl backdrop-blur-md border border-slate-700/80">
            <button
              type="button"
              onClick={handleZoomOut}
              className="w-8 h-8 rounded-xl bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-200 hover:text-white transition-all shadow-sm active:scale-95"
              title="Diminuir Zoom (-)"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span
              className="text-xs font-mono font-bold px-2 py-1 bg-slate-950/60 rounded-lg min-w-[52px] text-center text-blue-400 select-none"
              title="Escala atual"
            >
              {Math.round(stageScale * 100)}%
            </span>
            <button
              type="button"
              onClick={handleZoomIn}
              className="w-8 h-8 rounded-xl bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-200 hover:text-white transition-all shadow-sm active:scale-95"
              title="Aumentar Zoom (+)"
            >
              <Plus className="w-4 h-4" />
            </button>
            <div className="w-px h-5 bg-slate-700 mx-0.5" />
            <button
              type="button"
              onClick={handleZoomReset}
              className="px-2.5 py-1.5 text-xs font-semibold rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-all flex items-center gap-1 active:scale-95"
              title="Resetar Zoom e Centralizar tela (100%)"
            >
              <Maximize2 className="w-3.5 h-3.5 text-emerald-400" />
              <span className="hidden sm:inline">100%</span>
            </button>
          </div>

          {/* Barra Flutuante de Ação Rápida para Tablet / Mobile quando um elemento está selecionado */}
          {selectedElement && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-slate-900/95 backdrop-blur-md text-white px-3 py-1.5 rounded-2xl shadow-2xl border border-slate-700/80 flex items-center gap-2 z-30 pointer-events-auto">
              <span className="text-xs font-bold text-slate-200 truncate max-w-[120px] sm:max-w-none">
                {selectedElement.type === 'door_arc' ? '🚪 Porta' :
                 selectedElement.type === 'window' ? '🪟 Janela' :
                 selectedElement.type === 'circle' ? '◯ Círculo' :
                 selectedElement.type === 'appliance' ? `📦 ${selectedElement.name || 'Eletro'}` :
                 selectedElement.type === 'rectangle' ? '▢ Retângulo' :
                 selectedElement.type === 'dimension' ? '📏 Cota' :
                 'Elemento'}
              </span>

              {['door_arc', 'window', 'appliance', 'technical_point'].includes(selectedElement.type) && (
                <button
                  type="button"
                  onClick={() => rotateSelectedElement(90)}
                  className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-500 active:scale-95 text-white font-bold rounded-xl text-xs flex items-center gap-1 shadow-sm transition-all"
                  title="Girar 90 graus"
                >
                  <RotateCw className="w-3.5 h-3.5" />
                  <span>Girar 90°</span>
                </button>
              )}

              {selectedElement.type === 'door_arc' && (
                <button
                  type="button"
                  onClick={flipSelectedElement}
                  className="px-2.5 py-1 bg-slate-700 hover:bg-slate-600 active:scale-95 text-white font-semibold rounded-xl text-xs flex items-center gap-1 transition-all"
                  title="Inverter abertura da porta"
                >
                  <span>⇄ Inverter</span>
                </button>
              )}

              <button
                type="button"
                onClick={() => setShowMobileProperties(true)}
                className="px-2.5 py-1 bg-amber-500 hover:bg-amber-400 active:scale-95 text-slate-950 font-bold rounded-xl text-xs flex items-center gap-1 shadow-sm transition-all"
                title="Ajustar dimensões e propriedades"
              >
                <SlidersHorizontal className="w-3.5 h-3.5" />
                <span>Ajustar</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  deleteElement(selectedElement.id);
                  setSelectedId(null);
                }}
                className="p-1.5 bg-red-600 hover:bg-red-500 active:scale-95 text-white rounded-xl transition-all"
                title="Excluir elemento"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Input de texto flutuante */}
          {showTextInput && (
            <div
              style={{
                position: 'absolute',
                left: textInputPos.x,
                top: textInputPos.y - 5,
                zIndex: 1000
              }}
            >
              <input
                ref={textInputRef}
                type="text"
                value={textInputValue}
                onChange={(e) => {
                  textInputValueRef.current = e.target.value;
                  setTextInputValue(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    handleTextSubmit();
                  }
                  if (e.key === 'Escape') {
                    setShowTextInput(false);
                    setTextInputValue('');
                    textInputValueRef.current = '';
                    setTool('select');
                  }
                }}
                onBlur={() => {
                  setTimeout(() => {
                    handleTextSubmit();
                  }, 100);
                }}
                placeholder="Digite o texto..."
                style={{
                  padding: '8px 12px',
                  border: `2px solid ${brushColor}`,
                  borderRadius: '4px',
                  fontSize: '16px',
                  backgroundColor: 'white',
                  minWidth: '150px',
                  outline: 'none'
                }}
              />
            </div>
          )}
        </div>

        {/* Mobile Bottom Toolbar */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-2 py-2 z-10">
          <div className="flex items-center justify-around gap-1">
            <button
              onClick={() => setTool('select')}
              className={`p-3 rounded-lg ${tool === 'select' ? 'bg-green-100 text-green-600' : 'text-gray-600 hover:bg-gray-100'}`}
              title="Selecionar"
            >
              <MousePointer className="w-5 h-5" />
            </button>
            <button
              onClick={() => setTool('rectangle')}
              className={`p-3 rounded-lg ${tool === 'rectangle' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
              title="Retângulo"
            >
              <Square className="w-5 h-5" />
            </button>
            <button
              onClick={() => setTool('circle')}
              className={`p-3 rounded-lg ${tool === 'circle' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
              title="Círculo"
            >
              <CircleIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setTool('door_arc')}
              className={`p-3 rounded-lg ${tool === 'door_arc' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600 hover:bg-gray-100'}`}
              title="Porta"
            >
              <DoorOpen className="w-5 h-5" />
            </button>
            <button
              onClick={() => setTool('window')}
              className={`p-3 rounded-lg ${tool === 'window' ? 'bg-sky-100 text-sky-700' : 'text-gray-600 hover:bg-gray-100'}`}
              title="Janela"
            >
              <AppWindow className="w-5 h-5" />
            </button>
            <button
              onClick={() => setTool('line')}
              className={`p-3 rounded-lg ${tool === 'line' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
              title="Linha"
            >
              <Ruler className="w-5 h-5" />
            </button>
            <button
              onClick={() => setTool('dimension')}
              className={`p-3 rounded-lg ${tool === 'dimension' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600 hover:bg-gray-100'}`}
              title="Cota"
            >
              <Compass className="w-5 h-5" />
            </button>
            <button
              onClick={() => setTool('technical_point')}
              className={`p-3 rounded-lg ${tool === 'technical_point' ? 'bg-amber-100 text-amber-700' : 'text-gray-600 hover:bg-gray-100'}`}
              title="Ponto Técnico"
            >
              <Zap className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                if (tool === 'pen') {
                  setPenMode(prev => prev === 'freehand' ? 'line' : 'freehand');
                } else {
                  setTool('pen');
                }
              }}
              className={`p-3 rounded-lg ${tool === 'pen' ? 'bg-orange-100 text-orange-600 font-bold' : 'text-gray-600 hover:bg-gray-100'}`}
              title={`Caneta - Modo: ${penMode === 'freehand' ? 'Livre' : 'Reta'}`}
            >
              <PenTool className="w-5 h-5" />
            </button>
            <button
              onClick={() => setTool('eraser')}
              className={`p-3 rounded-lg ${tool === 'eraser' ? 'bg-rose-100 text-rose-600 font-bold' : 'text-gray-600 hover:bg-gray-100'}`}
              title="Apagar (Borracha)"
            >
              <Eraser className="w-5 h-5" />
            </button>
            <button
              onClick={() => setTool('text')}
              className={`p-3 rounded-lg ${tool === 'text' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
              title="Texto"
            >
              <Type className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowGrid(!showGrid)}
              className={`p-3 rounded-lg ${showGrid ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
              title="Grade"
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowCatalogDrawer(true)}
              className="p-3 rounded-lg text-teal-600 hover:bg-teal-50"
              title="Eletros & Objetos"
            >
              <Package className="w-5 h-5" />
            </button>
            <label
              htmlFor="image-upload-mobile"
              className={`p-3 rounded-lg cursor-pointer text-gray-600 hover:bg-gray-100 ${backgroundImage ? 'bg-purple-100 text-purple-600' : ''}`}
              title="Imagem"
            >
              <Image className="w-5 h-5" />
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload-mobile"
            />
            <button
              onClick={() => selectedId && deleteElement(selectedId) && setSelectedId(null)}
              disabled={!selectedId}
              className={`p-3 rounded-lg disabled:opacity-30 ${selectedId ? 'text-red-600 hover:bg-red-50' : 'text-gray-400'}`}
              title="Deletar"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Painel de Propriedades e Camadas - Desktop / Tablet */}
        {isSidebarOpen && (
          <div className="hidden md:flex w-80 bg-white border-l border-slate-200 flex flex-col z-20 shadow-xl transition-all">
            <div className="p-3 border-b border-slate-200 bg-slate-900 text-white">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1 bg-slate-800 p-0.5 rounded-lg border border-slate-700">
                  <button
                    type="button"
                    onClick={() => setSidebarTab('properties')}
                    className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${
                      sidebarTab === 'properties'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    ⚙️ Propriedades
                  </button>
                  <button
                    type="button"
                    onClick={() => setSidebarTab('layers')}
                    className={`px-3 py-1 rounded-md text-xs font-semibold transition-all flex items-center gap-1 ${
                      sidebarTab === 'layers'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <Layers className="w-3 h-3" />
                    <span>Camadas ({activePage.layers.length})</span>
                  </button>
                </div>
                {selectedId && (
                  <button
                    onClick={() => setSelectedId(null)}
                    className="text-[11px] px-2 py-0.5 bg-slate-800 border border-slate-700 text-slate-300 rounded hover:bg-slate-700 hover:text-white"
                  >
                    ✕ Fechar
                  </button>
                )}
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
            {sidebarTab === 'layers' ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">Camadas da {activePage.name}</h3>
                    <p className="text-[11px] text-slate-500">Organize elementos em níveis independentes</p>
                  </div>
                  <button
                    type="button"
                    onClick={addLayer}
                    className="px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold flex items-center gap-1 shadow-sm"
                    title="Adicionar Nova Camada"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Camada</span>
                  </button>
                </div>

                <div className="space-y-2">
                  {activePage.layers.map(layer => {
                    const isActive = activeLayerId === layer.id;
                    const elemCount = layer.elements?.length || 0;
                    return (
                      <div
                        key={layer.id}
                        onClick={() => layer.visible && setActiveLayerId(layer.id)}
                        className={`p-2.5 rounded-xl border transition-all flex items-center justify-between cursor-pointer ${
                          isActive
                            ? 'bg-blue-50/80 border-blue-400 shadow-sm ring-1 ring-blue-400/30'
                            : layer.visible
                            ? 'bg-white border-slate-200 hover:border-slate-300'
                            : 'bg-slate-100 border-slate-200 opacity-60'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleLayerVisibility(layer.id);
                              if (!layer.visible) setActiveLayerId(layer.id);
                            }}
                            className={`p-1.5 rounded-lg transition-colors ${
                              layer.visible 
                                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                                : 'bg-slate-200 text-slate-500 hover:bg-slate-300'
                            }`}
                            title={layer.visible ? 'Ocultar Camada' : 'Exibir Camada'}
                          >
                            {layer.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                          </button>
                          <input
                            type="text"
                            value={layer.name}
                            onChange={(e) => updateLayerName(layer.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs font-semibold text-slate-800 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-blue-500 focus:bg-white focus:outline-none px-1 py-0.5 rounded w-full truncate"
                          />
                        </div>

                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded font-mono font-medium">
                            {elemCount} {elemCount === 1 ? 'item' : 'itens'}
                          </span>
                          {activePage.layers.length > 1 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(`Excluir ${layer.name}? Elementos contidos serão removidos.`)) {
                                  deleteLayer(layer.id);
                                }
                              }}
                              className="p-1 text-slate-400 hover:text-red-500 rounded hover:bg-red-50"
                              title="Excluir Camada"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : selectedElement ? (
              <div className="space-y-4">
                <div className="text-sm font-medium text-gray-700 mb-4">
                  {selectedElement.type === 'rectangle' ? '▢ Retângulo' :
                   selectedElement.type === 'line' ? '─ Linha' :
                   selectedElement.type === 'dimension' ? '📏 Cota de Medição' :
                   selectedElement.type === 'technical_point' ? '⚡ Ponto de Instalação' :
                   selectedElement.type === 'freehand' ? '✏️ Desenho Livre' :
                   selectedElement.type === 'text' ? 'T Texto' : 'Elemento'}
                </div>

                {/* Edição de Ponto Técnico */}
                {selectedElement.type === 'technical_point' && (
                  <div className="space-y-3 p-3 bg-amber-50/60 border border-amber-200 rounded-xl">
                    <div>
                      <label className="block text-xs font-bold text-amber-900 mb-1">Tipo de Instalação</label>
                      <select
                        value={selectedElement.pointType || 'outlet_mid'}
                        onChange={(e) => {
                          const val = e.target.value;
                          const def = TECHNICAL_POINTS_CATALOG.find(p => p.id === val);
                          updateElement(selectedElement.id, {
                            pointType: val,
                            pointCategory: def?.category || 'electrical',
                            heightFromFloorCm: def?.defaultHeight || selectedElement.heightFromFloorCm
                          });
                        }}
                        className="w-full text-xs p-2 bg-white border border-amber-300 rounded-lg font-medium"
                      >
                        {TECHNICAL_POINTS_CATALOG.map(p => (
                          <option key={p.id} value={p.id}>{p.icon} {p.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-amber-900 mb-1">Altura do Piso Acabado (cm)</label>
                      <input
                        type="number"
                        min="0"
                        max="300"
                        value={selectedElement.heightFromFloorCm ?? 110}
                        onChange={(e) => updateElement(selectedElement.id, { heightFromFloorCm: parseInt(e.target.value, 10) || 0 })}
                        className="w-full text-xs p-2 bg-white border border-amber-300 rounded-lg font-bold"
                        placeholder="Ex: 110"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-amber-900 mb-1">Observações / Detalhe</label>
                      <input
                        type="text"
                        value={selectedElement.notes || ''}
                        onChange={(e) => updateElement(selectedElement.id, { notes: e.target.value })}
                        className="w-full text-xs p-2 bg-white border border-amber-300 rounded-lg"
                        placeholder="Ex: Ponto da coifa / filtro"
                      />
                    </div>
                  </div>
                )}

                {/* Edição de Cota Manual */}
                {selectedElement.type === 'dimension' && (
                  <div className="space-y-3 p-3 bg-indigo-50/60 border border-indigo-200 rounded-xl">
                    <label className="block text-xs font-bold text-indigo-900 mb-1">Valor da Medida ({project?.unit || 'cm'})</label>
                    <input
                      type="number"
                      value={selectedElement.measurements?.length ?? 0}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        updateElement(selectedElement.id, {
                          measurements: { ...selectedElement.measurements, length: val }
                        });
                      }}
                      className="w-full text-xs p-2 bg-white border border-indigo-300 rounded-lg font-bold text-indigo-950"
                    />
                    <p className="text-[11px] text-indigo-700">Ajuste o valor para sobrepor a medida real conferida na trena.</p>
                  </div>
                )}

                {selectedElement.type === 'text' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Texto
                      </label>
                      <textarea
                        value={selectedElement.text || ''}
                        onChange={(e) => {
                          setElements(prev => prev.map(el =>
                            el.id === selectedId ? { ...el, text: e.target.value } : el
                          ));
                        }}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tamanho da Fonte
                      </label>
                      <input
                        type="number"
                        min="8"
                        max="72"
                        value={selectedElement.fontSize || 16}
                        onChange={(e) => {
                          setElements(prev => prev.map(el =>
                            el.id === selectedId ? { ...el, fontSize: Number(e.target.value) } : el
                          ));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cor do Texto
                      </label>
                      <input
                        type="color"
                        value={selectedElement.color || '#000000'}
                        onChange={(e) => {
                          setElements(prev => prev.map(el =>
                            el.id === selectedId ? { ...el, color: e.target.value } : el
                          ));
                        }}
                        className="w-full h-10 rounded cursor-pointer"
                      />
                    </div>
                  </>
                )}

                {selectedElement.type === 'freehand' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cor do Traço
                      </label>
                      <input
                        type="color"
                        value={selectedElement.color || brushColor}
                        onChange={(e) => {
                          setElements(prev => prev.map(el =>
                            el.id === selectedId ? { ...el, color: e.target.value } : el
                          ));
                        }}
                        className="w-full h-10 rounded cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Espessura ({selectedElement.strokeWidth || brushSize}px)
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={selectedElement.strokeWidth || brushSize}
                        onChange={(e) => {
                          setElements(prev => prev.map(el =>
                            el.id === selectedId ? { ...el, strokeWidth: Number(e.target.value) } : el
                          ));
                        }}
                        className="w-full"
                      />
                    </div>
                  </>
                )}

          {/* Edição de Círculo / Pilar */}
          {selectedElement.type === 'circle' && (
            <div className="space-y-3 p-3 bg-blue-50/60 border border-blue-200 rounded-xl">
              <div>
                <span className="text-xs font-bold text-blue-950 block mb-1">◯ Círculo / Pilar</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-bold text-blue-900 mb-1">Diâmetro (cm)</label>
                  <input
                    type="number"
                    step="1"
                    min="4"
                    value={Math.round((selectedElement.radius || 30) * 2)}
                    onChange={(e) => {
                      const diam = parseFloat(e.target.value) || 10;
                      updateElement(selectedElement.id, {
                        radius: Math.round(diam / 2),
                        measurements: { diameter: diam }
                      });
                    }}
                    className="w-full text-xs p-2 bg-white border border-blue-300 rounded-lg font-bold"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-blue-900 mb-1">Raio (cm)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="2"
                    value={Math.round(selectedElement.radius || 30)}
                    onChange={(e) => {
                      const r = parseFloat(e.target.value) || 5;
                      updateElement(selectedElement.id, {
                        radius: r,
                        measurements: { diameter: r * 2 }
                      });
                    }}
                    className="w-full text-xs p-2 bg-white border border-blue-300 rounded-lg font-bold"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Edição de Porta / Arco de Abertura */}
          {selectedElement.type === 'door_arc' && (
            <div className="space-y-3 p-3 bg-indigo-50/60 border border-indigo-200 rounded-xl">
              <div>
                <span className="text-xs font-bold text-indigo-950 block mb-1">🚪 Porta (Arco 90°)</span>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-indigo-900 mb-1">Largura do Vão (cm)</label>
                <input
                  type="number"
                  step="1"
                  min="40"
                  max="200"
                  value={selectedElement.width || 80}
                  onChange={(e) => updateElement(selectedElement.id, { width: parseFloat(e.target.value) || 80 })}
                  className="w-full text-xs p-2 bg-white border border-indigo-300 rounded-lg font-bold"
                  placeholder="Ex: 80"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-indigo-900 mb-1.5">Posição de Abertura:</label>
                <div className="grid grid-cols-4 gap-1.5 mb-2">
                  {[0, 90, 180, 270].map(deg => (
                    <button
                      key={deg}
                      type="button"
                      onClick={() => updateElement(selectedElement.id, { rotation: deg })}
                      className={`py-1.5 text-xs font-bold rounded-lg border transition-all ${
                        (selectedElement.rotation || 0) === deg
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {deg}°
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => rotateSelectedElement(90)}
                    className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-1 shadow-xs transition-all"
                  >
                    <RotateCw className="w-3.5 h-3.5" />
                    <span>Girar 90°</span>
                  </button>
                  <button
                    type="button"
                    onClick={flipSelectedElement}
                    className="flex-1 py-2 bg-slate-100 hover:bg-slate-200 active:scale-95 text-slate-800 rounded-lg text-xs font-bold flex items-center justify-center gap-1 border border-slate-300 transition-all"
                  >
                    <span>⇄ Inverter</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edição de Janela Técnica 2D */}
          {selectedElement.type === 'window' && (
            <div className="space-y-3 p-3 bg-sky-50/60 border border-sky-200 rounded-xl">
              <div>
                <span className="text-xs font-bold text-sky-950 block mb-1">🪟 Janela Técnica 2D</span>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-sky-900 mb-1">Largura do Vão (cm)</label>
                <input
                  type="number"
                  step="1"
                  min="20"
                  max="400"
                  value={selectedElement.width || 100}
                  onChange={(e) => updateElement(selectedElement.id, { width: parseFloat(e.target.value) || 100 })}
                  className="w-full text-xs p-2 bg-white border border-sky-300 rounded-lg font-bold"
                  placeholder="Ex: 120"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-bold text-sky-900 mb-1">Altura (cm)</label>
                  <input
                    type="number"
                    step="1"
                    min="20"
                    value={selectedElement.alturaCm ?? 120}
                    onChange={(e) => updateElement(selectedElement.id, { alturaCm: parseInt(e.target.value, 10) || 120 })}
                    className="w-full text-xs p-2 bg-white border border-sky-300 rounded-lg font-bold"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-sky-900 mb-1">Peitoril (cm)</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={selectedElement.peitorilCm ?? 100}
                    onChange={(e) => updateElement(selectedElement.id, { peitorilCm: parseInt(e.target.value, 10) || 100 })}
                    className="w-full text-xs p-2 bg-white border border-sky-300 rounded-lg font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-sky-900 mb-1.5">Posição / Giro:</label>
                <div className="grid grid-cols-4 gap-1.5 mb-2">
                  {[0, 90, 180, 270].map(deg => (
                    <button
                      key={deg}
                      type="button"
                      onClick={() => updateElement(selectedElement.id, { rotation: deg })}
                      className={`py-1.5 text-xs font-bold rounded-lg border transition-all ${
                        (selectedElement.rotation || 0) === deg
                          ? 'bg-sky-600 text-white border-sky-600 shadow-xs'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {deg}°
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => rotateSelectedElement(90)}
                  className="w-full py-2 bg-sky-600 hover:bg-sky-700 active:scale-95 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-1 shadow-xs transition-all"
                >
                  <RotateCw className="w-3.5 h-3.5" />
                  <span>Girar 90° (Atual: {selectedElement.rotation || 0}°)</span>
                </button>
              </div>
            </div>
          )}

          {/* Edição de Retângulo */}
          {selectedElement.type === 'rectangle' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Largura (cm)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="1"
                  value={Math.round(selectedElement.width * 10) / 10}
                  onChange={(e) => updateMeasurement('width', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Altura (cm)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="1"
                  value={Math.round(selectedElement.height * 10) / 10}
                  onChange={(e) => updateMeasurement('height', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </>
          )}

          {/* Edição de Objeto do Catálogo / Eletrodoméstico */}
          {selectedElement.type === 'appliance' && (
            <div className="space-y-3 p-3 bg-teal-50/60 border border-teal-200 rounded-xl">
              <div>
                <span className="text-xs font-bold text-teal-950 block mb-1">Objeto / Eletrodoméstico</span>
                <p className="text-sm font-semibold text-teal-900">{selectedElement.name || 'Eletro 2D'}</p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-bold text-teal-900 mb-1">Largura (cm)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="10"
                    value={selectedElement.width || 60}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 10;
                      updateElement(selectedElement.id, { width: val });
                    }}
                    className="w-full text-xs p-2 bg-white border border-teal-300 rounded-lg font-bold"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-teal-900 mb-1">Profund. (cm)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="10"
                    value={selectedElement.height || 60}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 10;
                      updateElement(selectedElement.id, { height: val });
                    }}
                    className="w-full text-xs p-2 bg-white border border-teal-300 rounded-lg font-bold"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-[11px] font-bold text-teal-900">Rotação: {selectedElement.rotation || 0}°</label>
                  <button
                    type="button"
                    onClick={() => rotateSelectedElement(90)}
                    className="px-2.5 py-1 bg-teal-600 hover:bg-teal-700 text-white rounded text-[11px] font-bold flex items-center gap-1 shadow-2xs active:scale-95 transition-all"
                  >
                    <RotateCw className="w-3 h-3" />
                    <span>Girar 90° (R)</span>
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-1.5 mb-2">
                  {[0, 90, 180, 270].map(deg => (
                    <button
                      key={deg}
                      type="button"
                      onClick={() => updateElement(selectedElement.id, { rotation: deg })}
                      className={`py-1 text-xs font-bold rounded-lg border transition-all ${
                        (selectedElement.rotation || 0) === deg
                          ? 'bg-teal-600 text-white border-teal-600 shadow-xs'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {deg}°
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

                {selectedElement.type === 'line' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Comprimento (cm)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="1"
                        value={Math.round((selectedElement.measurements?.length || 0) * 10) / 10}
                        onChange={(e) => updateMeasurement('length', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ângulo (°)
                      </label>
                      <input
                        type="number"
                        step="1"
                        value={Math.round((selectedElement.measurements?.angle ?? 0) * 10) / 10}
                        onChange={(e) => updateMeasurement('angle', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        0° = para a direita, 90° = para cima
                      </p>
                    </div>
                  </>
                )}
                
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setElements(prev => prev.filter(el => el.id !== selectedId));
                      setSelectedId(null);
                    }}
                    className="w-full flex items-center justify-center space-x-2 bg-red-50 text-red-600 px-4 py-2 rounded-lg hover:bg-red-100"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Deletar Elemento</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-3">
                  <Square className="w-12 h-12 mx-auto" />
                </div>
                <p className="text-sm text-gray-500">
                  Selecione um elemento para editar suas propriedades
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Use as ferramentas à esquerda para desenhar
                </p>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Modal Inferior de Propriedades para Mobile / Tablet */}
      {showMobileProperties && selectedElement && (
        <div className="md:hidden fixed inset-x-0 bottom-16 bg-white border-t-2 border-blue-500 rounded-t-2xl shadow-2xl p-4 z-40 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-3">
            <span className="font-bold text-sm text-gray-900">
              Ajustar: {selectedElement.type}
            </span>
            <button
              type="button"
              onClick={() => setShowMobileProperties(false)}
              className="text-xs px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg font-semibold"
            >
              Concluir ✓
            </button>
          </div>

          {selectedElement.type === 'technical_point' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Tipo de Ponto</label>
                <select
                  value={selectedElement.pointType || 'outlet_mid'}
                  onChange={(e) => {
                    const val = e.target.value;
                    const def = TECHNICAL_POINTS_CATALOG.find(p => p.id === val);
                    updateElement(selectedElement.id, {
                      pointType: val,
                      pointCategory: def?.category || 'electrical',
                      heightFromFloorCm: def?.defaultHeight || selectedElement.heightFromFloorCm
                    });
                  }}
                  className="w-full text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-lg font-medium"
                >
                  {TECHNICAL_POINTS_CATALOG.map(p => (
                    <option key={p.id} value={p.id}>{p.icon} {p.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Altura do Piso (cm)</label>
                <input
                  type="number"
                  value={selectedElement.heightFromFloorCm ?? 110}
                  onChange={(e) => updateElement(selectedElement.id, { heightFromFloorCm: parseInt(e.target.value, 10) || 0 })}
                  className="w-full text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-lg font-bold"
                />
              </div>
            </div>
          )}

          {selectedElement.type === 'dimension' && (
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Valor da Medida ({project?.unit || 'cm'})</label>
              <input
                type="number"
                value={selectedElement.measurements?.length ?? 0}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) || 0;
                  updateElement(selectedElement.id, {
                    measurements: { ...selectedElement.measurements, length: val }
                  });
                }}
                className="w-full text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-lg font-bold"
              />
            </div>
          )}

          {selectedElement.type === 'rectangle' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Largura (cm)</label>
                <input
                  type="number"
                  step="0.1"
                  value={Math.round(selectedElement.width * 10) / 10}
                  onChange={(e) => updateMeasurement('width', e.target.value)}
                  className="w-full text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-lg font-bold"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Altura (cm)</label>
                <input
                  type="number"
                  step="0.1"
                  value={Math.round(selectedElement.height * 10) / 10}
                  onChange={(e) => updateMeasurement('height', e.target.value)}
                  className="w-full text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-lg font-bold"
                />
              </div>
            </div>
          )}

          {/* Mobile Círculo */}
          {selectedElement.type === 'circle' && (
            <div className="space-y-3">
              <span className="text-xs font-bold text-blue-950 block">◯ Círculo / Pilar</span>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Diâmetro (cm)</label>
                  <input
                    type="number"
                    step="1"
                    min="4"
                    value={Math.round((selectedElement.radius || 30) * 2)}
                    onChange={(e) => {
                      const diam = parseFloat(e.target.value) || 10;
                      updateElement(selectedElement.id, {
                        radius: Math.round(diam / 2),
                        measurements: { diameter: diam }
                      });
                    }}
                    className="w-full text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-lg font-bold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Raio (cm)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="2"
                    value={Math.round(selectedElement.radius || 30)}
                    onChange={(e) => {
                      const r = parseFloat(e.target.value) || 5;
                      updateElement(selectedElement.id, {
                        radius: r,
                        measurements: { diameter: r * 2 }
                      });
                    }}
                    className="w-full text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-lg font-bold"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Mobile Porta */}
          {selectedElement.type === 'door_arc' && (
            <div className="space-y-3">
              <span className="text-xs font-bold text-indigo-950 block">🚪 Porta (Arco 90°)</span>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Largura do Vão (cm)</label>
                <input
                  type="number"
                  step="1"
                  min="40"
                  max="200"
                  value={selectedElement.width || 80}
                  onChange={(e) => updateElement(selectedElement.id, { width: parseFloat(e.target.value) || 80 })}
                  className="w-full text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-lg font-bold"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Posição / Giro:</label>
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {[0, 90, 180, 270].map(deg => (
                    <button
                      key={deg}
                      type="button"
                      onClick={() => updateElement(selectedElement.id, { rotation: deg })}
                      className={`py-2 text-xs font-bold rounded-xl border ${
                        (selectedElement.rotation || 0) === deg
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                          : 'bg-gray-100 text-gray-700 border-gray-200'
                      }`}
                    >
                      {deg}°
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => rotateSelectedElement(90)}
                    className="flex-1 py-2.5 bg-indigo-600 active:scale-95 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1 shadow-xs"
                  >
                    <RotateCw className="w-3.5 h-3.5" />
                    <span>Girar 90°</span>
                  </button>
                  <button
                    type="button"
                    onClick={flipSelectedElement}
                    className="flex-1 py-2.5 bg-gray-100 active:scale-95 text-gray-800 font-bold rounded-xl text-xs flex items-center justify-center gap-1 border border-gray-300"
                  >
                    <span>⇄ Inverter</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Mobile Janela */}
          {selectedElement.type === 'window' && (
            <div className="space-y-3">
              <span className="text-xs font-bold text-sky-950 block">🪟 Janela Técnica 2D</span>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Largura do Vão (cm)</label>
                <input
                  type="number"
                  step="1"
                  min="20"
                  max="400"
                  value={selectedElement.width || 100}
                  onChange={(e) => updateElement(selectedElement.id, { width: parseFloat(e.target.value) || 100 })}
                  className="w-full text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-lg font-bold"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Altura (cm)</label>
                  <input
                    type="number"
                    step="1"
                    min="20"
                    value={selectedElement.alturaCm ?? 120}
                    onChange={(e) => updateElement(selectedElement.id, { alturaCm: parseInt(e.target.value, 10) || 120 })}
                    className="w-full text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-lg font-bold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Peitoril (cm)</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={selectedElement.peitorilCm ?? 100}
                    onChange={(e) => updateElement(selectedElement.id, { peitorilCm: parseInt(e.target.value, 10) || 100 })}
                    className="w-full text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-lg font-bold"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Posição / Giro:</label>
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {[0, 90, 180, 270].map(deg => (
                    <button
                      key={deg}
                      type="button"
                      onClick={() => updateElement(selectedElement.id, { rotation: deg })}
                      className={`py-2 text-xs font-bold rounded-xl border ${
                        (selectedElement.rotation || 0) === deg
                          ? 'bg-sky-600 text-white border-sky-600 shadow-xs'
                          : 'bg-gray-100 text-gray-700 border-gray-200'
                      }`}
                    >
                      {deg}°
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => rotateSelectedElement(90)}
                  className="w-full py-2.5 bg-sky-600 active:scale-95 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1 shadow-xs"
                >
                  <RotateCw className="w-3.5 h-3.5" />
                  <span>Girar 90° (Atual: {selectedElement.rotation || 0}°)</span>
                </button>
              </div>
            </div>
          )}

          {/* Mobile Eletro / Objeto */}
          {selectedElement.type === 'appliance' && (
            <div className="space-y-3">
              <div>
                <span className="text-xs font-bold text-teal-950 block mb-1">Objeto / Eletrodoméstico</span>
                <p className="text-sm font-semibold text-teal-900">{selectedElement.name || 'Eletro 2D'}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Largura (cm)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="10"
                    value={selectedElement.width || 60}
                    onChange={(e) => updateElement(selectedElement.id, { width: parseFloat(e.target.value) || 10 })}
                    className="w-full text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-lg font-bold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Profund. (cm)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="10"
                    value={selectedElement.height || 60}
                    onChange={(e) => updateElement(selectedElement.id, { height: parseFloat(e.target.value) || 10 })}
                    className="w-full text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-lg font-bold"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Girar Objeto:</label>
                <button
                  type="button"
                  onClick={() => rotateSelectedElement(90)}
                  className="w-full py-2.5 bg-teal-600 active:scale-95 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 shadow-xs"
                >
                  <RotateCw className="w-4 h-4" />
                  <span>Girar 90° (Atual: {selectedElement.rotation || 0}°)</span>
                </button>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              setElements(prev => prev.filter(el => el.id !== selectedId));
              setSelectedId(null);
              setShowMobileProperties(false);
            }}
            className="w-full mt-4 p-2 bg-red-50 text-red-600 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5"
          >
            <Trash2 className="w-4 h-4" />
            Excluir Elemento
          </button>
        </div>
      )}

      {/* Modal Mobile / Tablet de Gerenciamento de Camadas */}
      {showMobileLayersModal && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/60 flex items-end">
          <div className="w-full bg-white rounded-t-2xl shadow-2xl p-4 max-h-[75vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between pb-3 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-blue-600" />
                <h3 className="font-bold text-sm text-gray-900">Camadas da {activePage.name}</h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={addLayer}
                  className="px-2.5 py-1 bg-blue-600 text-white rounded-lg text-xs font-bold flex items-center gap-1 shadow-sm"
                >
                  <Plus className="w-3 h-3" />
                  <span>Nova</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowMobileLayersModal(false)}
                  className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto py-3 space-y-2">
              {activePage.layers.map(layer => {
                const isActive = activeLayerId === layer.id;
                const elemCount = layer.elements?.length || 0;
                return (
                  <div
                    key={layer.id}
                    onClick={() => {
                      if (layer.visible) {
                        setActiveLayerId(layer.id);
                        setShowMobileLayersModal(false);
                      }
                    }}
                    className={`p-3 rounded-xl border flex items-center justify-between transition-all ${
                      isActive
                        ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-400/20'
                        : layer.visible
                        ? 'bg-gray-50 border-gray-200'
                        : 'bg-gray-100 border-gray-200 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0 flex-1 mr-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleLayerVisibility(layer.id);
                          if (!layer.visible) setActiveLayerId(layer.id);
                        }}
                        className={`p-2 rounded-lg ${layer.visible ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'}`}
                        title={layer.visible ? 'Ocultar' : 'Exibir'}
                      >
                        {layer.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <span className="text-xs font-bold text-gray-800 truncate">
                        {layer.name}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-[10px] px-2 py-0.5 bg-white border border-gray-200 text-gray-600 rounded font-mono font-medium">
                        {elemCount} itens
                      </span>
                      {activePage.layers.length > 1 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Excluir ${layer.name}?`)) {
                              deleteLayer(layer.id);
                            }
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-500 rounded hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SketchEditor;
